import type { QueryClient } from '@tanstack/query-core'
import { queryCollectionOptions } from '@tanstack/query-db-collection'
import { createCollection, localOnlyCollectionOptions } from '@tanstack/db'
import PocketBase, { type RecordModel } from 'pocketbase'

import type { Addendum, AddendumChange, AppSettings, Branding, Callout, Customer, CustomerDocument, CustomerNote, CustomerReminder, CustomerRevision, Document, Job, JobAssignment, Kiosk, KioskPairedDevice, KioskSession, Note, NoteRevision, ServiceAssignment, ServiceRevision, ServiceStatus, SessionSubtype, StatusChangeLog, WhatsAppMessage, WhatsAppTemplateConfig, WhatsAppTwilioTemplate } from '@/lib/schemas/db'
import { normalizeToUtcIso } from '@/lib/datetime'
import { getRuntimeEnv } from '@/lib/runtimeEnv'
import { KIOSK_CLIENT_ORIGIN } from '../clientOrigin'
import { createRefetchScheduler, type RefetchScheduler, type RefetchTarget } from './realtimeRefetch'

import type { AuthApiPort, AuthStorePort, IdGeneratorPort, SaveBrandingInput, SaveWhatsAppTwilioSettingsInput, UserLookupPort, WhatsAppTwilioSettings } from '../ports'
import type { AuthResponse, User } from '../domain/models'
import type {
  ArchivedServiceRow,
  ArchivedServiceSortKey,
  ArchivedServiceType,
  ArchivedServicesSearchInput,
  ArchivedServicesSearchResult,
} from '../domain/archivedServices'
import type {
  CustomerSearchSortKey,
  CustomersSearchInput,
  CustomersSearchResult,
} from '../domain/customerSearch'
import type {
  ArchiveCustomerInput,
  CustomerArchivePreview,
  ServiceCollectionName,
} from '../domain/customerArchive'
import type {
  FollowUp,
  FollowUpCategory,
  FollowUpEvent,
  FollowUpSearchSortKey,
  FollowUpServiceType,
  FollowUpStatus,
  FollowUpsSearchInput,
  FollowUpsSearchResult,
  IntervalUnit,
  ScheduleStep,
} from '../domain/followUps'
import {
  defaultFollowUpSort,
  dueSyncHorizonUtcIso,
  upcomingBoundaryUtcIso,
} from '../domain/followUps'

function normalizeBaseUrl(raw: string) {
  const trimmed = raw.trim()
  if (trimmed.startsWith('/')) {
    const normalized = trimmed.replace(/\/+$/, '')
    return normalized.length > 0 ? normalized : '/'
  }
  const withProtocol = trimmed.includes('://')
    ? trimmed
    : `http://${trimmed}`
  return withProtocol.replace(/\/+$/, '')
}

function getAuthCollectionName(model: any, fallback: string) {
  const explicit = typeof model?.collectionName === 'string' ? model.collectionName.trim() : ''
  if (explicit) return explicit

  const fromId = typeof model?.collectionId === 'string' ? model.collectionId.trim() : ''
  if (fromId === 'pbc_1777206000') {
    return 'kiosk_tablet_users'
  }

  return fallback
}

function modelToUser(model: any, pb: PocketBase): User | null {
  if (!model) return null

  const id = String(model.id ?? '')
  if (!id) return null

  const email = String(model.email ?? '')
  const name = String(model.name ?? model.username ?? email ?? id)
  const createdAt = normalizePbUtcIso(String(model.created ?? model.createdAt ?? ''))
  const updatedAt = normalizePbUtcIso(String(model.updated ?? model.updatedAt ?? ''))

  const avatarFilename =
    typeof model.avatar === 'string' && model.avatar.length > 0
      ? model.avatar
      : undefined

  const avatarUrl =
    avatarFilename && typeof pb.files?.getURL === 'function'
      ? pb.files.getURL(model, avatarFilename)
      : undefined

  return {
    id,
    publicId: typeof model.public_id === 'string' ? model.public_id : undefined,
    createdAt,
    updatedAt,
    name,
    email,
    avatarUrl,
    deviceUuid: typeof model.device_uuid === 'string' ? model.device_uuid : undefined,
    isAdmin: model.is_admin === true,
    kioskId: typeof model.kiosk_id === 'string' ? model.kiosk_id : Array.isArray(model.kiosk_id) ? model.kiosk_id[0] : undefined,
  }
}

type PbSystemFields = {
  id: string
  created: string
  updated: string
}

function normalizePbUtcIso(value: string) {
  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/.test(trimmed)) {
    return trimmed.replace(' ', 'T')
  }
  return trimmed
}

type PbCustomerRecord = PbSystemFields & {
  public_id?: string
  full_name: string
  external_id?: string
  contact_address?: string
  contact_email?: string
  contact_phone?: string
  invoice_name: string
  invoice_address: string
  invoice_vat?: string
  invoice_email: string
  invoice_phone: string
  external_customer_id?: string
  archived_at?: string
  archived_by?: string
  archive_reason?: string
  merged_into?: string
}

type PbWhatsAppMessageRecord = PbSystemFields & {
  customer_id?: string | string[]
  service_id?: string
  service_public_id?: string
  author_id?: string
  reminder_id?: string
  to_phone: string
  content: string
  template_sid?: string
  template_variables?: Record<string, string> | null
  status: WhatsAppMessage['status']
  send_after: string
  sent_at?: string
  cancelled_at?: string
  failure_reason?: string
  provider_sid?: string
}

type PbAppSettingsRecord = PbSystemFields & {
  whatsapp_send_delay_seconds: number
  whatsapp_sending_enabled?: boolean
  reminder_schedule_seconds?: number[] | null
  reminder_repeat_seconds?: number
  reminder_max_age_days?: number
}

type PbCustomerReminderRecord = PbSystemFields & {
  customer_id: string
  state: CustomerReminder['state']
  started_at?: string
  joined_at?: string
  last_sent_at?: string
  sent_count?: number
  next_send_at?: string
  status_note?: string
  stopped_at?: string
  stopped_by?: string
  archived_at?: string
}

type PbBrandingRecord = PbSystemFields & {
  name: string
  logo?: string
  logo_on_light?: string
}

type PbWhatsAppTemplateRecord = PbSystemFields & {
  key: string
  label?: string
  content_sid?: string
  variables?: Record<string, string> | null
}

type PbWhatsAppTwilioTemplateRecord = PbSystemFields & {
  content_sid: string
  friendly_name?: string
  language?: string
  variables?: Record<string, string> | null
  body?: string
  synced_at?: string
}

type PbKioskRecord = PbSystemFields & {
  name: string
  label: string
  friendly_name?: string
}

type PbKioskSessionRecord = PbSystemFields & {
  kiosk_id: string | string[]
  kiosk_type?: KioskSession['kioskType']
  flow: KioskSession['flow']
  current_step: number
  form_data?: KioskSession['formData']
  job_id?: string | string[]
  last_completed_flow?: 'check-in' | 'check-out'
  started_by?: string
  pairing_code?: string
  pairing_code_expires_at?: string
  last_update_origin?: string
}

type PbDocumentRecord = PbSystemFields & {
  key: string
  title: string
  body: string
}

type PbKioskTabletUserDeviceRecord = RecordModel & PbSystemFields & {
  kiosk_id: string | string[]
  device_uuid?: string
  paired_by_user_id?: string | string[]
  revoked_by_user_id?: string | string[]
  last_pairing_id?: string | string[]
  pairing_code?: string
  friendly_name?: string
  status?: KioskPairedDevice['status']
  user_agent?: string
  paired_at?: string
  last_seen_at?: string
  revoked_at?: string
}

type PbJobRecord = RecordModel & PbSystemFields & {
  public_id?: string
  customer_id: string | string[]
  author_id?: string
  status: Job['status']
  billing_outcome?: Job['billingOutcome']
  contact_address?: string
  contact_name?: string
  contact_phone?: string
  contact_email?: string
  invoice_name?: string
  invoice_address?: string
  invoice_vat?: string
  invoice_email?: string
  invoice_phone?: string
  device_type?: string
  device_description?: string
  device_password?: string
  fault: string
  peripherals?: string
  number_tag?: string
  checked_in_signed_at?: string
  checked_in_by?: string
  checked_in_no_signature_reason?: string
  checked_in_signature?: string | string[]
  checked_out_signed_at?: string
  checked_out_by?: string
  checked_out_name?: string
  checked_out_phone?: string
  checked_out_no_signature_reason?: string
  checked_out_signature?: string | string[]
  ready_for_collection_at?: string
  collected_at?: string
  invoice_id?: string
}

type PbCalloutRecord = PbSystemFields & {
  public_id?: string
  customer_id: string | string[]
  author_id?: string
  start_time: string
  end_time: string
  is_whole_day: boolean
  status: Callout['status']
  issue_summary: string
  contact_address: string
  contact_name?: string
  contact_phone?: string
  contact_email?: string
  invoice_name: string
  invoice_address?: string
  invoice_vat?: string
  invoice_email?: string
  invoice_phone?: string
  invoice_id?: string
  billables_consumed?: unknown
  audit_logs?: unknown
  status_changes?: unknown
  session_subtype?: SessionSubtype
}

type PbCalloutNoteRecord = PbSystemFields & {
  callout_id: string | string[]
  author_id: string
  text?: string
  caption?: string
  photo?: string | string[]
  photos?: unknown
  revisions?: NoteRevision[] | null
  deleted_at?: string
  deleted_by?: string
}

type PbSessionNoteRecord = PbSystemFields & {
  session_id: string | string[]
  author_id: string
  text?: string
  caption?: string
  photo?: string | string[]
  photos?: unknown
  revisions?: NoteRevision[] | null
  deleted_at?: string
  deleted_by?: string
}

type PbJobNoteRecord = PbSystemFields & {
  job_id: string | string[]
  author_id: string
  text?: string
  caption?: string
  photo?: string | string[]
  photos?: unknown
  revisions?: NoteRevision[] | null
  deleted_at?: string
  deleted_by?: string
}

type PbCustomerNoteRecord = PbSystemFields & {
  customer_id: string | string[]
  author_id: string
  text?: string
  caption?: string
  photo?: string | string[]
  photos?: unknown
  revisions?: NoteRevision[] | null
  deleted_at?: string
  deleted_by?: string
  follow_up_id?: string | string[]
}

type PbCustomerDocumentRecord = PbSystemFields & {
  customer_id: string | string[]
  title: string
  file?: string | string[]
  size?: number
  uploaded_by: string
  deleted_at?: string
  deleted_by?: string
}

type PbFollowUpRecord = PbSystemFields & {
  customer_id: string | string[]
  title: string
  details?: string
  service_type?: FollowUpServiceType | ''
  service_id?: string
  due_at: string
  schedule?: ScheduleStep[] | string | null
  schedule_index?: number
  category_id?: string
  status: FollowUpStatus
  last_done_at?: string
  history?: FollowUpEvent[] | string | null
  assignee_id?: string
  created_by: string
  closed_at?: string
  closed_by?: string
}

type PbAddendumRecord = PbSystemFields & {
  callout_id?: string | string[]
  session_id?: string | string[]
  job_id?: string | string[]
  author_id: string
  reason: string
  changes?: AddendumChange[] | string | null
}

type PbCustomerRevisionRecord = PbSystemFields & {
  customer_id: string | string[]
  author_id: string
  changes?: AddendumChange[] | string | null
}

type PbServiceRevisionRecord = PbSystemFields & {
  callout_id?: string | string[]
  session_id?: string | string[]
  job_id?: string | string[]
  author_id: string
  changes?: AddendumChange[] | string | null
}

type PbCalloutStatusChangeRecord = PbSystemFields & {
  callout_id: string | string[]
  author_id: string
  previous_status: ServiceStatus
  new_status: ServiceStatus
  occurred_at?: string
  reason?: string
}

type PbSessionStatusChangeRecord = PbSystemFields & {
  session_id: string | string[]
  author_id: string
  previous_status: ServiceStatus
  new_status: ServiceStatus
  occurred_at?: string
  reason?: string
}

type PbJobStatusChangeRecord = PbSystemFields & {
  job_id: string | string[]
  author_id: string
  previous_status: ServiceStatus
  new_status: ServiceStatus
  occurred_at?: string
  reason?: string
}

type PbJobAssignmentRecord = PbSystemFields & {
  job_id: string | string[]
  user_id: string | string[]
  author_id: string
  assigned_at?: string
  unassigned_at?: string
}

function coerceSingleId(value: string | string[]) {
  return Array.isArray(value) ? value[0] ?? '' : value
}

function coerceSingleFile(value: string | string[] | undefined) {
  if (!value) return ''
  return Array.isArray(value) ? value[0] ?? '' : value
}

/**
 * The photos a note currently shows, in display order.
 *
 * `photos` (json) is authoritative when present — it is rewritten on every
 * edit of a multi-photo note, by which point the client knows the stored
 * filenames from its own read. Otherwise derive from the retain-everything
 * file field: every attached file not named by a revision is current (a fresh
 * group note has no revisions, so all its files show). If that subtraction
 * leaves nothing but files exist — a legacy note whose caption edits recorded
 * its own photo into revisions — the last attached file is current, which is
 * the pre-group meaning of "current".
 */
function resolveCurrentPhotoFilenames(record: {
  photo?: string | string[]
  photos?: unknown
  revisions?: unknown
}): string[] {
  const files = !record.photo
    ? []
    : Array.isArray(record.photo)
      ? record.photo
      : [record.photo]
  if (files.length === 0) return []

  const listed = parseJsonColumn(record.photos)
  if (Array.isArray(listed)) {
    const current = listed.filter(
      (name): name is string =>
        typeof name === 'string' && files.includes(name),
    )
    if (current.length > 0) return current
  }

  const superseded = new Set<string>()
  const revisions = parseJsonColumn(record.revisions)
  if (Array.isArray(revisions)) {
    for (const entry of revisions) {
      const revision = (entry ?? {}) as Record<string, unknown>
      if (typeof revision.photo === 'string') superseded.add(revision.photo)
      if (Array.isArray(revision.photos)) {
        for (const name of revision.photos) {
          if (typeof name === 'string') superseded.add(name)
        }
      }
    }
  }

  const current = files.filter((name) => !superseded.has(name))
  return current.length > 0 ? current : [files[files.length - 1]]
}

function pbCustomerToCustomer(record: PbCustomerRecord): Customer {
  return {
    id: record.id,
    publicId: record.public_id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    fullName: record.full_name,
    externalId: record.external_id,
    contactAddress: record.contact_address,
    contactEmail: record.contact_email,
    contactPhone: record.contact_phone,
    invoiceName: record.invoice_name,
    invoiceAddress: record.invoice_address,
    invoiceVat: record.invoice_vat,
    invoiceEmail: record.invoice_email,
    invoicePhone: record.invoice_phone,
    externalCustomerId: record.external_customer_id,
    archivedAt: record.archived_at ? normalizePbUtcIso(record.archived_at) : undefined,
    archivedBy: record.archived_by || undefined,
    archiveReason: record.archive_reason || undefined,
    mergedInto: record.merged_into || undefined,
  }
}

function customerToPbInput(customer: Customer) {
  return {
    full_name: customer.fullName,
    external_id: customer.externalId,
    contact_address: customer.contactAddress,
    contact_email: customer.contactEmail,
    contact_phone: customer.contactPhone,
    invoice_name: customer.invoiceName,
    invoice_address: customer.invoiceAddress,
    invoice_vat: customer.invoiceVat,
    invoice_email: customer.invoiceEmail,
    invoice_phone: customer.invoicePhone,
    external_customer_id: customer.externalCustomerId,
  } satisfies Record<string, unknown>
}

function customerToPbCreateInput(customer: Customer) {
  return { id: customer.id, ...customerToPbInput(customer) } satisfies Record<
    string,
    unknown
  >
}

function pbWhatsAppMessageToWhatsAppMessage(
  record: PbWhatsAppMessageRecord,
): WhatsAppMessage {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    customerId: record.customer_id ? coerceSingleId(record.customer_id) || undefined : undefined,
    serviceId: record.service_id || undefined,
    servicePublicId: record.service_public_id || undefined,
    authorId: record.author_id || undefined,
    reminderId: record.reminder_id || undefined,
    toPhone: record.to_phone,
    content: record.content,
    templateSid: record.template_sid || undefined,
    templateVariables:
      record.template_variables && Object.keys(record.template_variables).length
        ? record.template_variables
        : undefined,
    status: record.status,
    sendAfter: normalizePbUtcIso(record.send_after),
    sentAt: record.sent_at ? normalizePbUtcIso(record.sent_at) : undefined,
    cancelledAt: record.cancelled_at
      ? normalizePbUtcIso(record.cancelled_at)
      : undefined,
    failureReason: record.failure_reason || undefined,
    providerSid: record.provider_sid || undefined,
  }
}

function whatsAppMessageToPbInput(message: WhatsAppMessage) {
  return {
    customer_id: message.customerId ?? '',
    service_id: message.serviceId ?? '',
    service_public_id: message.servicePublicId ?? '',
    author_id: message.authorId ?? '',
    reminder_id: message.reminderId ?? '',
    to_phone: message.toPhone,
    content: message.content,
    template_sid: message.templateSid ?? '',
    template_variables: message.templateVariables ?? null,
    status: message.status,
    send_after: message.sendAfter,
    sent_at: message.sentAt ?? '',
    cancelled_at: message.cancelledAt ?? '',
    failure_reason: message.failureReason ?? '',
    provider_sid: message.providerSid ?? '',
  } satisfies Record<string, unknown>
}

function whatsAppMessageToPbCreateInput(message: WhatsAppMessage) {
  return { id: message.id, ...whatsAppMessageToPbInput(message) } satisfies Record<
    string,
    unknown
  >
}

function pbAppSettingsToAppSettings(record: PbAppSettingsRecord): AppSettings {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    whatsappSendDelaySeconds: record.whatsapp_send_delay_seconds,
    whatsappSendingEnabled: record.whatsapp_sending_enabled ?? false,
    // PocketBase reports an unset JSON field as null and an unset number as 0.
    // The schedule is the "has the admin configured reminders?" marker: while
    // it is null the server uses its built-in defaults for all of them.
    reminderScheduleSeconds: record.reminder_schedule_seconds ?? undefined,
    reminderRepeatSeconds: record.reminder_schedule_seconds
      ? record.reminder_repeat_seconds
      : undefined,
    reminderMaxAgeDays: record.reminder_max_age_days || undefined,
  }
}

function pbCustomerReminderToReminder(
  record: PbCustomerReminderRecord,
): CustomerReminder {
  const optional = (value?: string) => (value ? normalizePbUtcIso(value) : undefined)
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    customerId: record.customer_id,
    state: record.state,
    startedAt: optional(record.started_at),
    joinedAt: optional(record.joined_at),
    lastSentAt: optional(record.last_sent_at),
    sentCount: record.sent_count ?? 0,
    nextSendAt: optional(record.next_send_at),
    statusNote: record.status_note ?? '',
    stoppedAt: optional(record.stopped_at),
    stoppedBy: record.stopped_by || undefined,
    archivedAt: optional(record.archived_at),
  }
}

function pbWhatsAppTemplateToConfig(
  record: PbWhatsAppTemplateRecord,
): WhatsAppTemplateConfig {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    key: record.key,
    label: record.label ?? '',
    contentSid: record.content_sid || undefined,
    variables: record.variables ?? {},
  }
}

function pbWhatsAppTwilioTemplateToTemplate(
  record: PbWhatsAppTwilioTemplateRecord,
): WhatsAppTwilioTemplate {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    contentSid: record.content_sid,
    friendlyName: record.friendly_name || undefined,
    language: record.language || undefined,
    variables: record.variables ?? {},
    body: record.body || undefined,
    syncedAt: record.synced_at ? normalizePbUtcIso(record.synced_at) : undefined,
  }
}

function normalizeUtcIsoInput(value: string) {
  const normalized = normalizePbUtcIso(value).trim()
  if (!normalized) return normalized

  const noTimezoneIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(normalized)
  if (noTimezoneIso) {
    return `${normalized}Z`
  }

  return normalizeToUtcIso(normalized)
}

function assertUtcIso(value: string, field: 'startTime' | 'endTime') {
  const normalized = value.trim()
  const isUtcIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?Z$/.test(normalized)
  if (!isUtcIso) {
    throw new Error(`${field} must be ISO UTC (ending in Z). Received: ${value}`)
  }
}

function pbKioskToKiosk(record: PbKioskRecord): Kiosk {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    name: record.name,
    label: record.label,
    friendlyName: record.friendly_name || undefined,
  }
}

function kioskToPbInput(kiosk: Kiosk) {
  return {
    name: kiosk.name,
    label: kiosk.label,
    friendly_name: kiosk.friendlyName ?? null,
  } satisfies Record<string, unknown>
}

function kioskToPbCreateInput(kiosk: Kiosk) {
  return { id: kiosk.id, ...kioskToPbInput(kiosk) } satisfies Record<string, unknown>
}

function pbKioskSessionToKioskSession(record: PbKioskSessionRecord): KioskSession {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    kioskId: coerceSingleId(record.kiosk_id),
    kioskType: record.kiosk_type ?? 'check-in',
    flow: record.flow,
    currentStep: record.current_step,
    formData: record.form_data ?? {},
    jobId: record.job_id ? coerceSingleId(record.job_id) : undefined,
    lastCompletedFlow: record.last_completed_flow,
    startedBy: record.started_by || undefined,
    pairingCode: record.pairing_code || undefined,
    pairingCodeExpiresAt: record.pairing_code_expires_at || undefined,
    lastUpdateOrigin: record.last_update_origin || undefined,
  }
}

function kioskSessionToPbInput(session: KioskSession) {
  const currentStep =
    typeof session.currentStep === 'number' && Number.isFinite(session.currentStep)
      ? Math.max(1, Math.trunc(session.currentStep))
      : 1

  return {
    kiosk_id: session.kioskId,
    kiosk_type: session.kioskType ?? 'check-in',
    flow: session.flow,
    current_step: currentStep,
    form_data: session.formData ?? {},
    job_id: session.jobId ?? null,
    last_completed_flow: session.lastCompletedFlow ?? null,
    started_by: session.startedBy ?? null,
    pairing_code: session.pairingCode ?? null,
    pairing_code_expires_at: session.pairingCodeExpiresAt ?? null,
    last_update_origin: KIOSK_CLIENT_ORIGIN,
  } satisfies Record<string, unknown>
}

function kioskSessionToPbCreateInput(session: KioskSession) {
  return { id: session.id, ...kioskSessionToPbInput(session) } satisfies Record<string, unknown>
}

function pbDocumentToDocument(record: PbDocumentRecord): Document {
  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    key: record.key,
    title: record.title,
    body: record.body,
  }
}

function documentToPbInput(document: Document) {
  return {
    key: document.key,
    title: document.title,
    body: document.body,
  } satisfies Record<string, unknown>
}

function documentToPbCreateInput(document: Document) {
  return { id: document.id, ...documentToPbInput(document) } satisfies Record<string, unknown>
}

function pbKioskTabletUserToKioskPairedDevice(record: PbKioskTabletUserDeviceRecord): KioskPairedDevice | null {
  const deviceUuid = typeof record.device_uuid === 'string' ? record.device_uuid.trim() : ''
  if (!deviceUuid) return null

  return {
    id: record.id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    kioskId: coerceSingleId(record.kiosk_id),
    kioskUserId: record.id,
    pairedByUserId: record.paired_by_user_id ? coerceSingleId(record.paired_by_user_id) : undefined,
    revokedByUserId: record.revoked_by_user_id ? coerceSingleId(record.revoked_by_user_id) : undefined,
    lastPairingId: record.last_pairing_id ? coerceSingleId(record.last_pairing_id) : undefined,
    deviceUuid,
    pairingCode: record.pairing_code || undefined,
    friendlyName: record.friendly_name || undefined,
    status: record.status ?? 'active',
    userAgent: record.user_agent || undefined,
    pairedAt: record.paired_at || undefined,
    lastSeenAt: record.last_seen_at || undefined,
    revokedAt: record.revoked_at || undefined,
  }
}

function kioskPairedDeviceToPbTabletUserInput(device: KioskPairedDevice) {
  return {
    kiosk_id: device.kioskId,
    device_uuid: device.deviceUuid,
    paired_by_user_id: device.pairedByUserId ?? null,
    revoked_by_user_id: device.revokedByUserId ?? null,
    last_pairing_id: device.lastPairingId ?? null,
    pairing_code: device.pairingCode ?? null,
    friendly_name: device.friendlyName ?? null,
    status: device.status,
    user_agent: device.userAgent ?? '',
    paired_at: device.pairedAt ?? null,
    last_seen_at: device.lastSeenAt ?? null,
    revoked_at: device.revokedAt ?? null,
  } satisfies Record<string, unknown>
}

function pbJobToJob(record: PbJobRecord): Job {
  return {
    id: record.id,
    publicId: record.public_id,
    customerId: coerceSingleId(record.customer_id),
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    authorId: record.author_id,
    status: record.status,
    billingOutcome: record.billing_outcome,
    contactAddress: record.contact_address,
    contactName: record.contact_name,
    contactPhone: record.contact_phone,
    contactEmail: record.contact_email,
    invoiceName: record.invoice_name,
    invoiceAddress: record.invoice_address,
    invoiceVat: record.invoice_vat,
    invoiceEmail: record.invoice_email,
    invoicePhone: record.invoice_phone,
    deviceType: record.device_type,
    deviceDescription: record.device_description,
    devicePassword: record.device_password,
    fault: record.fault,
    peripherals: record.peripherals,
    numberTag: record.number_tag,
    checkedInSignedAt: record.checked_in_signed_at,
    checkedInBy: record.checked_in_by,
    checkedInNoSignatureReason: record.checked_in_no_signature_reason,
    checkedOutSignedAt: record.checked_out_signed_at,
    checkedOutBy: record.checked_out_by,
    checkedOutName: record.checked_out_name,
    checkedOutPhone: record.checked_out_phone,
    checkedOutNoSignatureReason: record.checked_out_no_signature_reason,
    checkedInSignature: coerceSingleFile(record.checked_in_signature),
    checkedOutSignature: coerceSingleFile(record.checked_out_signature),
    readyForCollectionAt: record.ready_for_collection_at,
    collectedAt: record.collected_at,
    invoiceId: record.invoice_id,
  }
}

function jobToPbInput(job: Job) {
  const payload: Record<string, unknown> = {
    customer_id: job.customerId,
    author_id: job.authorId,
    status: job.status,
    billing_outcome: job.billingOutcome,
    contact_address: job.contactAddress,
    contact_name: job.contactName,
    contact_phone: job.contactPhone,
    contact_email: job.contactEmail,
    invoice_name: job.invoiceName,
    invoice_address: job.invoiceAddress,
    invoice_vat: job.invoiceVat,
    invoice_email: job.invoiceEmail,
    invoice_phone: job.invoicePhone,
    device_type: job.deviceType,
    device_description: job.deviceDescription,
    device_password: job.devicePassword,
    fault: job.fault,
    peripherals: job.peripherals,
    number_tag: job.numberTag,
    checked_in_signed_at: job.checkedInSignedAt,
    checked_in_by: job.checkedInBy,
    checked_in_no_signature_reason: job.checkedInNoSignatureReason,
    checked_out_signed_at: job.checkedOutSignedAt,
    checked_out_by: job.checkedOutBy,
    checked_out_name: job.checkedOutName,
    checked_out_phone: job.checkedOutPhone,
    checked_out_no_signature_reason: job.checkedOutNoSignatureReason,
    ready_for_collection_at: job.readyForCollectionAt,
    collected_at: job.collectedAt,
    invoice_id: job.invoiceId,
  }

  if (job.checkedInSignature) payload.checked_in_signature = job.checkedInSignature
  if (job.checkedOutSignature) payload.checked_out_signature = job.checkedOutSignature

  return payload
}

function jobToPbCreateInput(job: Job) {
  return { id: job.id, ...jobToPbInput(job) } satisfies Record<string, unknown>
}

function pbJobAssignmentToJobAssignment(record: PbJobAssignmentRecord): JobAssignment {
  return {
    id: record.id,
    jobId: coerceSingleId(record.job_id),
    userId: coerceSingleId(record.user_id),
    authorId: record.author_id,
    assignedAt: record.assigned_at,
    unassignedAt: record.unassigned_at,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
  }
}

function jobAssignmentToPbInput(assignment: JobAssignment) {
  return {
    job_id: assignment.jobId,
    user_id: assignment.userId,
    author_id: assignment.authorId,
    assigned_at: assignment.assignedAt,
    unassigned_at: assignment.unassignedAt,
  } satisfies Record<string, unknown>
}

function jobAssignmentToPbCreateInput(assignment: JobAssignment) {
  return { id: assignment.id, ...jobAssignmentToPbInput(assignment) } satisfies Record<string, unknown>
}

type PbServiceAssignmentRecord = PbSystemFields & {
  callout_id?: string | string[]
  session_id?: string | string[]
  user_id: string | string[]
  author_id: string
  assigned_at?: string
  unassigned_at?: string
}

function pbServiceAssignmentToServiceAssignment(
  record: PbServiceAssignmentRecord,
  relationField: 'callout_id' | 'session_id',
): ServiceAssignment {
  return {
    id: record.id,
    serviceId: coerceSingleId(record[relationField] ?? ''),
    userId: coerceSingleId(record.user_id),
    authorId: record.author_id,
    assignedAt: record.assigned_at,
    unassignedAt: record.unassigned_at,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
  }
}

function serviceAssignmentToPbInput(
  assignment: ServiceAssignment,
  relationField: 'callout_id' | 'session_id',
) {
  return {
    [relationField]: assignment.serviceId,
    user_id: assignment.userId,
    author_id: assignment.authorId,
    assigned_at: assignment.assignedAt,
    unassigned_at: assignment.unassignedAt,
  } satisfies Record<string, unknown>
}

function pbCalloutToCallout(record: PbCalloutRecord): Callout {
  return {
    id: record.id,
    publicId: record.public_id,
    customerId: coerceSingleId(record.customer_id),
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    authorId: record.author_id,
    startTime: record.start_time,
    endTime: record.end_time,
    isWholeDay: record.is_whole_day,
    status: record.status,
    issueSummary: record.issue_summary,
    contactAddress: record.contact_address,
    contactName: record.contact_name,
    contactPhone: record.contact_phone,
    contactEmail: record.contact_email,
    invoiceName: record.invoice_name,
    invoiceAddress: record.invoice_address,
    invoiceVat: record.invoice_vat,
    invoiceEmail: record.invoice_email,
    invoicePhone: record.invoice_phone,
    invoiceId: record.invoice_id,
    billablesConsumed: record.billables_consumed as Callout['billablesConsumed'],
    auditLogs: record.audit_logs as Callout['auditLogs'],
    statusChanges: record.status_changes as Callout['statusChanges'],
    sessionSubtype: record.session_subtype,
  }
}

function calloutToPbInput(callout: Callout) {
  const startTime = normalizeUtcIsoInput(callout.startTime)
  const endTime = normalizeUtcIsoInput(callout.endTime)

  assertUtcIso(startTime, 'startTime')
  assertUtcIso(endTime, 'endTime')

  const payload: Record<string, unknown> = {
    customer_id: callout.customerId,
    author_id: callout.authorId,
    start_time: startTime,
    end_time: endTime,
    is_whole_day: callout.isWholeDay,
    status: callout.status,
    issue_summary: callout.issueSummary,
    contact_address: callout.contactAddress,
    contact_name: callout.contactName,
    contact_phone: callout.contactPhone,
    contact_email: callout.contactEmail,
    invoice_name: callout.invoiceName,
    invoice_address: callout.invoiceAddress,
    invoice_vat: callout.invoiceVat,
    invoice_email: callout.invoiceEmail,
    invoice_phone: callout.invoicePhone,
    invoice_id: callout.invoiceId,
    billables_consumed: callout.billablesConsumed ?? [],
    audit_logs: callout.auditLogs ?? [],
    status_changes: callout.statusChanges ?? [],
  }

  if (callout.sessionSubtype) {
    payload.session_subtype = callout.sessionSubtype
  }

  return payload
}

function calloutToPbCreateInput(callout: Callout) {
  return { id: callout.id, ...calloutToPbInput(callout) } satisfies Record<
    string,
    unknown
  >
}

function pbNoteToNote(
  record: PbCalloutNoteRecord | PbSessionNoteRecord | PbJobNoteRecord,
  pb: PocketBase,
  relationField: 'callout_id' | 'session_id' | 'job_id',
): Note {
  // `photo` holds every version this note has ever shown, oldest first;
  // superseded entries stay referenced purely so the files survive. The
  // currently shown group is resolved separately, and its first photo is the
  // cover exposed through the legacy single-photo fields.
  const photoFilenames = resolveCurrentPhotoFilenames(record)
  const photoFilename = photoFilenames[0] ?? ''
  const resolveFileUrl = (filename: string) =>
    filename && typeof pb.files?.getURL === 'function'
      ? pb.files.getURL(record, filename)
      : undefined
  const photoUrl = resolveFileUrl(photoFilename)
  const photoUrls = photoFilenames
    .map((filename) => resolveFileUrl(filename))
    .filter((url): url is string => Boolean(url))

  const relatedId =
    relationField === 'callout_id'
      ? coerceSingleId((record as PbCalloutNoteRecord).callout_id)
      : relationField === 'session_id'
        ? coerceSingleId((record as PbSessionNoteRecord).session_id)
        : coerceSingleId((record as PbJobNoteRecord).job_id)

  return {
    id: record.id,
    serviceId: relatedId,
    authorId: record.author_id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    text: record.text,
    caption: record.caption,
    photoUrl,
    photo: photoFilename || undefined,
    photoUrls: photoUrls.length > 0 ? photoUrls : undefined,
    photos: photoFilenames.length > 0 ? photoFilenames : undefined,
    revisions: normalizeNoteRevisions(record.revisions, resolveFileUrl),
    deletedAt: record.deleted_at
      ? normalizePbUtcIso(record.deleted_at)
      : undefined,
    deletedBy: record.deleted_by || undefined,
  }
}

/**
 * PocketBase hands back `json` columns as whatever was stored — including
 * `null` or, on some drivers, a JSON string. Normalize to a real array so
 * callers can rely on `revisions?.length`.
 */
function normalizeNoteRevisions(
  value: unknown,
  resolveFileUrl: (filename: string) => string | undefined,
): NoteRevision[] | undefined {
  const parsed = parseJsonColumn(value)
  if (!Array.isArray(parsed) || parsed.length === 0) return undefined
  return parsed.map((entry) => {
    const revision = (entry ?? {}) as Record<string, unknown>
    const photo = typeof revision.photo === 'string' ? revision.photo : undefined
    const photos = Array.isArray(revision.photos)
      ? revision.photos.filter(
          (name): name is string => typeof name === 'string',
        )
      : undefined
    const photoUrls = photos
      ?.map((name) => resolveFileUrl(name))
      .filter((url): url is string => Boolean(url))
    return {
      text: typeof revision.text === 'string' ? revision.text : undefined,
      caption:
        typeof revision.caption === 'string' ? revision.caption : undefined,
      photo,
      photoUrl: photo ? resolveFileUrl(photo) : undefined,
      photos: photos && photos.length > 0 ? photos : undefined,
      photoUrls: photoUrls && photoUrls.length > 0 ? photoUrls : undefined,
      at:
        typeof revision.at === 'string' ? normalizePbUtcIso(revision.at) : '',
      authorId: typeof revision.authorId === 'string' ? revision.authorId : '',
    }
  })
}

function parseJsonColumn(value: unknown): unknown {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

type NoteWithPhotoFile = Note & { photoFile?: File; photoFiles?: File[] }

function noteToPbInput(
  note: NoteWithPhotoFile,
  relationField: 'callout_id' | 'session_id' | 'job_id',
) {
  const payload: Record<string, unknown> = {
    author_id: note.authorId,
    text: note.text,
    caption: note.caption,
    // Must be sent explicitly: PocketBase only persists keys present in the
    // payload, so omitting these would silently drop every edit and delete.
    // `photoUrl` is stripped — it is derived from the filename at read time and
    // embeds the environment's base URL, which must never reach the database.
    revisions: (note.revisions ?? []).map(
      ({ photoUrl: _photoUrl, photoUrls: _photoUrls, ...revision }) => revision,
    ),
    // The current group's filenames. Only meaningful for multi-photo notes;
    // single-photo notes rely on the legacy last-file-is-current derivation,
    // and clearing here keeps a photo replacement from pinning a stale list.
    photos: (note.photos?.length ?? 0) > 1 ? note.photos : '',
    deleted_at: note.deletedAt ?? '',
    deleted_by: note.deletedBy ?? '',
  }

  if (relationField === 'callout_id') {
    payload.callout_id = note.serviceId
  } else if (relationField === 'session_id') {
    payload.session_id = note.serviceId
  } else {
    payload.job_id = note.serviceId
  }

  if (note.photoFiles?.length) {
    // `photo+` appends (a whole group at once here). Assigning `photo` would
    // replace the field's contents, and PocketBase deletes whatever a file
    // field stops referencing — which is exactly how superseded photos used to
    // be destroyed. Nothing is ever removed; previous versions stay attached.
    payload['photo+'] = note.photoFiles
  } else if (note.photoFile) {
    payload['photo+'] = note.photoFile
  }

  return payload
}

function noteToPbCreateInput(
  note: NoteWithPhotoFile,
  relationField: 'callout_id' | 'session_id' | 'job_id',
) {
  const { 'photo+': appendedPhoto, ...payload } = noteToPbInput(note, relationField)

  // The append modifier only means anything against an existing record; a new
  // note has nothing to append to, so its first photo is a plain assignment.
  if (appendedPhoto) payload.photo = appendedPhoto

  return { id: note.id, ...payload } satisfies Record<string, unknown>
}

/**
 * The customer thread's mapper.
 *
 * Deliberately a sibling of {@link pbNoteToNote} rather than another branch of
 * it: a customer note's parent is a customer, not a service, so it maps to
 * `customerId` and carries `followUpId`. Everything else — the photo-version
 * handling, the revision trail — is shared.
 */
function pbCustomerNoteToCustomerNote(
  record: PbCustomerNoteRecord,
  pb: PocketBase,
): CustomerNote {
  const photoFilenames = resolveCurrentPhotoFilenames(record)
  const photoFilename = photoFilenames[0] ?? ''
  const resolveFileUrl = (filename: string) =>
    filename && typeof pb.files?.getURL === 'function'
      ? pb.files.getURL(record, filename)
      : undefined
  const photoUrls = photoFilenames
    .map((filename) => resolveFileUrl(filename))
    .filter((url): url is string => Boolean(url))

  return {
    id: record.id,
    customerId: coerceSingleId(record.customer_id),
    authorId: record.author_id,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
    text: record.text,
    caption: record.caption,
    photoUrl: resolveFileUrl(photoFilename),
    photo: photoFilename || undefined,
    photoUrls: photoUrls.length > 0 ? photoUrls : undefined,
    photos: photoFilenames.length > 0 ? photoFilenames : undefined,
    revisions: normalizeNoteRevisions(record.revisions, resolveFileUrl),
    deletedAt: record.deleted_at
      ? normalizePbUtcIso(record.deleted_at)
      : undefined,
    deletedBy: record.deleted_by || undefined,
    followUpId: coerceSingleId(record.follow_up_id ?? '') || undefined,
  }
}

type CustomerNoteWithPhotoFile = CustomerNote & {
  photoFile?: File
  photoFiles?: File[]
}

function customerNoteToPbInput(note: CustomerNoteWithPhotoFile) {
  const payload: Record<string, unknown> = {
    customer_id: note.customerId,
    author_id: note.authorId,
    text: note.text,
    caption: note.caption,
    revisions: (note.revisions ?? []).map(
      ({ photoUrl: _photoUrl, photoUrls: _photoUrls, ...revision }) => revision,
    ),
    photos: (note.photos?.length ?? 0) > 1 ? note.photos : '',
    deleted_at: note.deletedAt ?? '',
    deleted_by: note.deletedBy ?? '',
    follow_up_id: note.followUpId ?? '',
  }

  // See noteToPbInput: `photo+` appends, so superseded images stay referenced
  // and survive.
  if (note.photoFiles?.length) {
    payload['photo+'] = note.photoFiles
  } else if (note.photoFile) {
    payload['photo+'] = note.photoFile
  }

  return payload
}

function customerNoteToPbCreateInput(note: CustomerNoteWithPhotoFile) {
  const { 'photo+': appendedPhoto, ...payload } = customerNoteToPbInput(note)

  if (appendedPhoto) payload.photo = appendedPhoto

  return { id: note.id, ...payload } satisfies Record<string, unknown>
}

/** Write-only File carried alongside the stored filename, as notes do. */
type CustomerDocumentWithFile = CustomerDocument & { file?: File }

function pbCustomerDocumentToCustomerDocument(
  record: PbCustomerDocumentRecord,
  pb: PocketBase,
): CustomerDocument {
  const fileName = Array.isArray(record.file)
    ? (record.file[0] ?? '')
    : (record.file ?? '')

  return {
    id: record.id,
    customerId: coerceSingleId(record.customer_id),
    title: record.title,
    fileName: fileName || undefined,
    fileUrl:
      fileName && typeof pb.files?.getURL === 'function'
        ? pb.files.getURL(record, fileName)
        : undefined,
    size: typeof record.size === 'number' && record.size > 0 ? record.size : undefined,
    uploadedBy: record.uploaded_by,
    deletedAt: record.deleted_at
      ? normalizePbUtcIso(record.deleted_at)
      : undefined,
    deletedBy: record.deleted_by || undefined,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
  }
}

function customerDocumentToPbInput(document: CustomerDocumentWithFile) {
  // The stored file never changes after upload — updates deliberately omit
  // the `file` key, since assigning it would replace the file and PocketBase
  // deletes any file a record stops referencing.
  return {
    customer_id: document.customerId,
    title: document.title,
    size: document.size ?? 0,
    uploaded_by: document.uploadedBy,
    deleted_at: document.deletedAt ?? '',
    deleted_by: document.deletedBy ?? '',
  } satisfies Record<string, unknown>
}

function customerDocumentToPbCreateInput(document: CustomerDocumentWithFile) {
  return {
    id: document.id,
    ...customerDocumentToPbInput(document),
    file: document.file,
  } satisfies Record<string, unknown>
}

function pbFollowUpToFollowUp(record: PbFollowUpRecord): FollowUp {
  const serviceType = record.service_type || undefined
  const serviceId = record.service_id || undefined

  return {
    id: record.id,
    customerId: coerceSingleId(record.customer_id),
    title: record.title,
    details: record.details || undefined,
    // Both halves or neither — a dangling type with no id says nothing.
    service:
      serviceType && serviceId
        ? { type: serviceType, id: serviceId }
        : undefined,
    dueAt: normalizePbUtcIso(record.due_at),
    schedule: normalizeScheduleColumn(record.schedule),
    scheduleIndex: record.schedule_index ?? 0,
    categoryId: record.category_id || undefined,
    status: record.status,
    lastDoneAt: record.last_done_at
      ? normalizePbUtcIso(record.last_done_at)
      : undefined,
    history: normalizeHistoryColumn(record.history),
    assigneeId: record.assignee_id || undefined,
    createdBy: record.created_by,
    closedAt: record.closed_at ? normalizePbUtcIso(record.closed_at) : undefined,
    closedBy: record.closed_by || undefined,
    createdAt: normalizePbUtcIso(record.created),
    updatedAt: normalizePbUtcIso(record.updated),
  }
}

/** `json` columns arrive as null, an array, or a JSON string. Normalize. */
function normalizeScheduleColumn(value: unknown): ScheduleStep[] {
  const parsed = parseJsonColumn(value)
  if (!Array.isArray(parsed)) return []

  return parsed.flatMap((entry): ScheduleStep[] => {
    const step = (entry ?? {}) as Record<string, unknown>
    if (typeof step.onDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(step.onDate)) {
      return [{ onDate: step.onDate }]
    }
    const amount = typeof step.amount === 'number' ? step.amount : undefined
    const unit = typeof step.unit === 'string' ? step.unit : undefined
    if (!amount || !unit) return []
    return [{ amount, unit: unit as IntervalUnit }]
  })
}

function normalizeHistoryColumn(value: unknown): FollowUpEvent[] {
  const parsed = parseJsonColumn(value)
  if (!Array.isArray(parsed)) return []

  return parsed.flatMap((entry) => {
    const event = (entry ?? {}) as Record<string, unknown>
    if (typeof event.type !== 'string' || typeof event.at !== 'string') return []
    return [
      {
        type: event.type as FollowUpEvent['type'],
        at: normalizePbUtcIso(event.at),
        by: typeof event.by === 'string' ? event.by : '',
        dueAt:
          typeof event.dueAt === 'string'
            ? normalizePbUtcIso(event.dueAt)
            : undefined,
        note: typeof event.note === 'string' ? event.note : undefined,
      },
    ]
  })
}

function followUpToPbInput(followUp: FollowUp) {
  return {
    customer_id: followUp.customerId,
    title: followUp.title,
    details: followUp.details ?? '',
    service_type: followUp.service?.type ?? '',
    service_id: followUp.service?.id ?? '',
    due_at: followUp.dueAt,
    schedule: followUp.schedule ?? [],
    schedule_index: followUp.scheduleIndex ?? 0,
    category_id: followUp.categoryId ?? '',
    status: followUp.status,
    // Sent explicitly so clearing them on reopen actually persists.
    last_done_at: followUp.lastDoneAt ?? '',
    history: followUp.history ?? [],
    assignee_id: followUp.assigneeId ?? '',
    created_by: followUp.createdBy,
    closed_at: followUp.closedAt ?? '',
    closed_by: followUp.closedBy ?? '',
  } satisfies Record<string, unknown>
}

function followUpToPbCreateInput(followUp: FollowUp) {
  return { id: followUp.id, ...followUpToPbInput(followUp) } satisfies Record<
    string,
    unknown
  >
}

function pbAddendumToAddendum(
  record: PbAddendumRecord,
  relationField: 'callout_id' | 'session_id' | 'job_id',
): Addendum {
  const relatedId = coerceSingleId(
    (record[relationField] ?? '') as string | string[],
  )

  const parsedChanges = parseJsonColumn(record.changes)
  const changes: AddendumChange[] = Array.isArray(parsedChanges)
    ? parsedChanges.map((entry) => {
        const change = (entry ?? {}) as Record<string, unknown>
        return {
          field: typeof change.field === 'string' ? change.field : '',
          label: typeof change.label === 'string' ? change.label : '',
          before: typeof change.before === 'string' ? change.before : '',
          after: typeof change.after === 'string' ? change.after : '',
        }
      })
    : []

  return {
    id: record.id,
    serviceId: relatedId,
    authorId: record.author_id,
    createdAt: normalizePbUtcIso(record.created),
    reason: record.reason ?? '',
    changes,
  }
}

function addendumToPbCreateInput(
  addendum: Addendum,
  relationField: 'callout_id' | 'session_id' | 'job_id',
) {
  return {
    id: addendum.id,
    [relationField]: addendum.serviceId,
    author_id: addendum.authorId,
    reason: addendum.reason,
    changes: addendum.changes,
  } satisfies Record<string, unknown>
}

function pbCustomerRevisionToCustomerRevision(
  record: PbCustomerRevisionRecord,
): CustomerRevision {
  const parsedChanges = parseJsonColumn(record.changes)
  const changes: AddendumChange[] = Array.isArray(parsedChanges)
    ? parsedChanges.map((entry) => {
        const change = (entry ?? {}) as Record<string, unknown>
        return {
          field: typeof change.field === 'string' ? change.field : '',
          label: typeof change.label === 'string' ? change.label : '',
          before: typeof change.before === 'string' ? change.before : '',
          after: typeof change.after === 'string' ? change.after : '',
        }
      })
    : []

  return {
    id: record.id,
    customerId: coerceSingleId(record.customer_id),
    authorId: record.author_id,
    createdAt: normalizePbUtcIso(record.created),
    changes,
  }
}

function pbServiceRevisionToServiceRevision(
  record: PbServiceRevisionRecord,
  relationField: 'callout_id' | 'session_id' | 'job_id',
): ServiceRevision {
  const parsedChanges = parseJsonColumn(record.changes)
  const changes: AddendumChange[] = Array.isArray(parsedChanges)
    ? parsedChanges.map((entry) => {
        const change = (entry ?? {}) as Record<string, unknown>
        return {
          field: typeof change.field === 'string' ? change.field : '',
          label: typeof change.label === 'string' ? change.label : '',
          before: typeof change.before === 'string' ? change.before : '',
          after: typeof change.after === 'string' ? change.after : '',
        }
      })
    : []

  return {
    id: record.id,
    serviceId: coerceSingleId((record[relationField] ?? '') as string | string[]),
    authorId: record.author_id,
    createdAt: normalizePbUtcIso(record.created),
    changes,
  }
}

function pbStatusChangeToStatusChangeLog(
  record: PbCalloutStatusChangeRecord | PbSessionStatusChangeRecord | PbJobStatusChangeRecord,
  relationField: 'callout_id' | 'session_id' | 'job_id',
): StatusChangeLog {
  const relatedId =
    relationField === 'callout_id'
      ? coerceSingleId((record as PbCalloutStatusChangeRecord).callout_id)
      : relationField === 'session_id'
        ? coerceSingleId((record as PbSessionStatusChangeRecord).session_id)
        : coerceSingleId((record as PbJobStatusChangeRecord).job_id)

  return {
    id: record.id,
    serviceId: relatedId,
    authorId: record.author_id,
    previousStatus: record.previous_status,
    newStatus: record.new_status,
    createdAt: normalizePbUtcIso(record.created),
    occurredAt: record.occurred_at
      ? normalizePbUtcIso(record.occurred_at)
      : undefined,
    reason: record.reason,
  }
}

function statusChangeLogToPbInput(
  statusChange: StatusChangeLog,
  relationField: 'callout_id' | 'session_id' | 'job_id',
) {
  const payload: Record<string, unknown> = {
    author_id: statusChange.authorId,
    previous_status: statusChange.previousStatus,
    new_status: statusChange.newStatus,
    occurred_at: statusChange.occurredAt ?? '',
    reason: statusChange.reason,
  }

  if (relationField === 'callout_id') {
    payload.callout_id = statusChange.serviceId
  } else if (relationField === 'session_id') {
    payload.session_id = statusChange.serviceId
  } else {
    payload.job_id = statusChange.serviceId
  }

  return payload
}

function statusChangeLogToPbCreateInput(
  statusChange: StatusChangeLog,
  relationField: 'callout_id' | 'session_id' | 'job_id',
) {
  return {
    id: statusChange.id,
    ...statusChangeLogToPbInput(statusChange, relationField),
  } satisfies Record<string, unknown>
}

type PbMutationResult = RecordModel | { id?: unknown } | null | undefined

function isPbRecord(result: unknown): result is RecordModel {
  return (
    !!result &&
    typeof result === 'object' &&
    'id' in result &&
    typeof (result as any).id === 'string' &&
    (result as any).id.length > 0 &&
    'collectionId' in result &&
    typeof (result as any).collectionId === 'string' &&
    'collectionName' in result &&
    typeof (result as any).collectionName === 'string'
  )
}

function requirePbRecord(
  collection: string,
  op: 'create' | 'update',
  key: string,
  result: PbMutationResult,
) {
  if (!isPbRecord(result)) {
    throw new Error(`PocketBase ${collection}.${op}(${key}) returned no record`)
  }
  if (result.id !== key) {
    throw new Error(
      `PocketBase ${collection}.${op}(${key}) returned record id=${result.id}`,
    )
  }
  return result
}

function requirePbDelete(collection: string, key: string, result: unknown) {
  if (result !== true) {
    throw new Error(`PocketBase ${collection}.delete(${key}) was unsuccessful`)
  }
}

export const pocketBaseIdGenerator: IdGeneratorPort = {
  generateId() {
    // PocketBase default record IDs are 15 chars of [a-z0-9].
    return Array.from({ length: 15 }, () =>
      Math.floor(Math.random() * 36).toString(36),
    ).join('')
  },
}

/**
 * Mirrors the auth token into a cookie scoped to the file endpoints, so
 * protected files (photos, signatures, documents) load from plain same-origin
 * URLs — the file-auth middleware in pb_hooks turns the cookie into a file
 * token server-side, which lets every `<img>` keep working without per-URL
 * token plumbing. JS-set rather than HttpOnly, which adds no exposure beyond
 * the token already living in localStorage. SameSite=Lax means other sites
 * embedding a file URL never send it, so hotlinking still 404s.
 */
function syncFileAuthCookie(token: string) {
  if (typeof document === 'undefined') return
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:'
      ? '; Secure'
      : ''
  // Max-Age tracks the auth token's own 14-day lifetime; a stale cookie just
  // fails validation server-side and the file 404s like any logged-out read.
  const attributes = token
    ? `; Path=/api/files; Max-Age=1209600; SameSite=Lax${secure}`
    : `; Path=/api/files; Max-Age=0; SameSite=Lax${secure}`
  document.cookie = `pb_file_auth=${token}${attributes}`
}

export function createPocketBaseAuth(opts?: {
  pb?: PocketBase
  baseUrl?: string
  authCollection?: string
}): {
  pb: PocketBase
  authStore: AuthStorePort
  authApi: AuthApiPort
} {
  const pb =
    opts?.pb ??
    (() => {
      const runtimeEnv = getRuntimeEnv()
      const baseUrl = opts?.baseUrl ?? runtimeEnv.VITE_POCKETBASE_URL ?? '/'
      return new PocketBase(normalizeBaseUrl(baseUrl))
    })()

  // fireImmediately: the persisted login must set the cookie on boot, not
  // only on the next explicit sign-in.
  pb.authStore.onChange((token) => syncFileAuthCookie(token), true)

  const authCollection =
    opts?.authCollection ?? getRuntimeEnv().VITE_POCKETBASE_AUTH_COLLECTION ?? 'users'

  const authStore: AuthStorePort = {
    get token() {
      const token = pb.authStore.token
      return token && token.length > 0 ? token : null
    },
    get user() {
      return modelToUser(pb.authStore.model, pb)
    },
    get isValid() {
      return pb.authStore.isValid
    },
    save(token: string, _user: User | null) {
      pb.authStore.save(token, pb.authStore.model)
    },
    clear() {
      pb.authStore.clear()
    },
  }

  const authApi: AuthApiPort = {
    async loginWithPassword(
      identity: string,
      password: string,
    ): Promise<AuthResponse> {
      const res = await pb
        .collection(authCollection)
        .authWithPassword(identity, password)
      const token = pb.authStore.token
      const user = modelToUser(res.record ?? pb.authStore.model, pb)
      if (!token || !user) {
        throw new Error('PocketBase login succeeded but returned no token/user')
      }
      return { token, user }
    },
    async refreshAuth(deviceUuid?: string): Promise<AuthResponse> {
      const currentCollection = getAuthCollectionName(pb.authStore.model, authCollection)
      const isKioskTabletAuth = currentCollection === 'kiosk_tablet_users'
      if (isKioskTabletAuth) {
        const res = await pb.send('/api/kiosk/device/refresh', {
          method: 'POST',
          body: { deviceUuid },
          $autoCancel: false,
        }) as { token?: string; record?: RecordModel }
        const token = typeof res?.token === 'string' && res.token.length > 0 ? res.token : pb.authStore.token
        const user = modelToUser(res?.record ?? pb.authStore.model, pb)
        if (!token || !user) {
          throw new Error('PocketBase refresh returned no token/user')
        }
        pb.authStore.save(token, (res?.record ?? pb.authStore.model) as any)
        return { token, user }
      }

      const res = await pb
        .collection(currentCollection)
        .authRefresh({ $autoCancel: false })
      const token = pb.authStore.token
      const user = modelToUser(res.record ?? pb.authStore.model, pb)
      if (!token || !user) {
        throw new Error('PocketBase refresh returned no token/user')
      }
      return { token, user }
    },
    async updateProfile(userId: string, input) {
      const payload: FormData | Record<string, unknown> = input.avatarFile
        ? (() => {
            const formData = new FormData()
            formData.append('name', input.name)
            formData.append('email', input.email)
            formData.append('avatar', input.avatarFile)
            return formData
          })()
        : (() => {
            const data: Record<string, unknown> = {
              name: input.name,
              email: input.email,
            }
            if (input.removeAvatar) {
              data.avatar = null
            }
            return data
          })()

      const currentCollection = getAuthCollectionName(pb.authStore.model, authCollection)
      const result = await pb
        .collection(currentCollection)
        .update(userId, payload)
      const record = requirePbRecord(
        authCollection,
        'update',
        userId,
        result as PbMutationResult,
      )
      const token = pb.authStore.token
      if (token) {
        pb.authStore.save(token, record)
      }
      const user = modelToUser(record, pb)
      if (!user) {
        throw new Error('PocketBase update profile returned no user')
      }
      return user
    },
    async updatePassword(userId: string, input) {
      const currentCollection = getAuthCollectionName(pb.authStore.model, authCollection)
      await pb.collection(currentCollection).update(userId, {
        oldPassword: input.currentPassword,
        password: input.newPassword,
        passwordConfirm: input.confirmPassword,
      })
    },
  }

  return { pb, authStore, authApi }
}

export function createPocketBaseAuthStore(opts?: {
  pb?: PocketBase
  baseUrl?: string
  authCollection?: string
}): AuthStorePort {
  return createPocketBaseAuth(opts).authStore
}

export function createPocketBaseAuthApi(opts?: {
  pb?: PocketBase
  baseUrl?: string
  authCollection?: string
}): AuthApiPort {
  return createPocketBaseAuth(opts).authApi
}

// Archived (Invoiced/Cancelled) services are excluded from the live sync so
// the browser doesn't accumulate the whole history: the Archived screen
// searches the archived_services view server-side, and detail pages hydrate
// individual archived records on demand via hydrateServiceDetails.
const activeServiceFilter = "status != 'Invoiced' && status != 'Cancelled'"
const activeParentFilter = (relation: 'callout_id' | 'session_id' | 'job_id') =>
  `${relation}.status != 'Invoiced' && ${relation}.status != 'Cancelled'`

type HydratedServiceType = 'callout' | 'session' | 'job'

/**
 * Archived services hydrated on demand for a detail page.
 *
 * The active-only queries above are the source of truth for their collections,
 * so a plain refetch (window focus does one by default) would EVICT any
 * archived record that hydrateServiceDetails had upserted — yanking a locked
 * record out from under its own detail page mid-edit. Every query that filters
 * to active services must widen its filter to also keep these.
 */
const hydratedServiceIds: Record<HydratedServiceType, Set<string>> = {
  callout: new Set(),
  session: new Set(),
  job: new Set(),
}

const HYDRATED_IDS_KEPT = 50

function rememberHydratedService(type: HydratedServiceType, id: string) {
  const ids = hydratedServiceIds[type]
  ids.delete(id)
  ids.add(id)
  // Bound the filter growth for very long sessions; Sets iterate in insertion
  // order, so the first entry is the least recently hydrated.
  while (ids.size > HYDRATED_IDS_KEPT) {
    const oldest = ids.values().next().value
    if (oldest === undefined) break
    ids.delete(oldest)
  }
}

/** `base`, widened so hydrated archived records survive a refetch. */
function keepingHydrated(
  base: string,
  type: HydratedServiceType,
  field: string = 'id',
): string {
  const ids = [...hydratedServiceIds[type]]
  if (ids.length === 0) return base
  const keep = ids.map((id) => `${field} = '${id}'`).join(' || ')
  return `(${base}) || ${keep}`
}

interface UpsertTarget<TRow> {
  preload: () => Promise<void>
  utils: { writeUpsert: (data: Partial<TRow> | Array<Partial<TRow>>) => void }
}

/**
 * Upsert into a collection, waiting for its sync to be initialised first.
 *
 * Every collection here is lazy (`startSync: false`), and `writeUpsert` throws
 * `SyncNotInitializedError` until sync has settled. Hydration races that
 * startup: a detail page that mounts with its record already missing calls
 * hydrate on its first commit, while the child collections its live queries
 * touch (notes, status changes, addendums, assignments) are still starting.
 * The write then threw and its data never landed — the classic "empty the
 * first time, fine after a refresh". `preload` is idempotent and shares one
 * in-flight promise, so waiting is cheap.
 */
async function upsertWhenReady<TRow>(
  target: UpsertTarget<TRow>,
  data: Partial<TRow> | Array<Partial<TRow>>,
): Promise<void> {
  if (Array.isArray(data) && data.length === 0) return
  await target.preload()
  target.utils.writeUpsert(data)
}

export function createPocketBaseTanStackDb(opts: {
  pb: PocketBase
  seedUsers: User[]
  queryClient: QueryClient
}) {
  const pb = opts.pb
  const authCollection =
    getRuntimeEnv().VITE_POCKETBASE_AUTH_COLLECTION ?? 'users'

  const usersCollection = createCollection(
    queryCollectionOptions<User, unknown, any, User['id']>({
      id: 'users',
      queryKey: ['pocketbase', 'users'],
      queryClient: opts.queryClient,
      getKey: (user) => user.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection(authCollection)
          .getFullList<RecordModel>({ sort: 'name' })

        return records
          .map((record) => modelToUser(record, pb))
          .filter((user): user is User => Boolean(user))
      },
      onInsert: async () => {
        throw new Error('users are managed by auth and cannot be created here')
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection(authCollection)
            .update<RecordModel>(expectedId, {
              name: mutation.modified.name,
              email: mutation.modified.email,
            })
          requirePbRecord(authCollection, 'update', expectedId, result)
        }
      },
      onDelete: async () => {
        throw new Error('users are managed by auth and cannot be deleted here')
      },
    }),
  )

  const followUpCategoriesCollection = createCollection(
    queryCollectionOptions<FollowUpCategory, unknown, any, FollowUpCategory['id']>({
      id: 'follow_up_categories',
      queryKey: ['pocketbase', 'follow_up_categories'],
      queryClient: opts.queryClient,
      getKey: (category) => category.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('follow_up_categories')
          .getFullList<RecordModel>({ sort: 'sort_order,name' })

        return records.map((record) => ({
          id: record.id,
          name: (record.name as string) ?? '',
          sortOrder: (record.sort_order as number) ?? 0,
        }))
      },
      // Admin-curated on the Settings page; the server enforces the gate
      // (follow_up_categories write rules require users.is_admin).
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const category = mutation.modified as FollowUpCategory
          const result = await pb
            .collection('follow_up_categories')
            .create<RecordModel>({
              id: category.id,
              name: category.name,
              sort_order: category.sortOrder,
            })
          requirePbRecord('follow_up_categories', 'create', category.id, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const category = mutation.modified as FollowUpCategory
          const result = await pb
            .collection('follow_up_categories')
            .update<RecordModel>(String(mutation.key), {
              name: category.name,
              sort_order: category.sortOrder,
            })
          requirePbRecord('follow_up_categories', 'update', String(mutation.key), result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          await pb.collection('follow_up_categories').delete(String(mutation.key))
        }
      },
    }),
  )

  const customersCollection = createCollection(
    queryCollectionOptions<Customer, unknown, any, Customer['id']>({
      id: 'customers',
      queryKey: ['pocketbase', 'customers'],
      queryClient: opts.queryClient,
      getKey: (customer) => customer.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('customers')
          .getFullList<PbCustomerRecord>({ sort: '-updated' })
        return records.map(pbCustomerToCustomer)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb.collection('customers').create<PbCustomerRecord>(
            customerToPbCreateInput(mutation.modified),
          )
          requirePbRecord('customers', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('customers')
            .update<PbCustomerRecord>(expectedId, customerToPbInput(mutation.modified))
          requirePbRecord('customers', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('customers').delete(expectedId)
          requirePbDelete('customers', expectedId, result)
        }
      },
    }),
  )

  const whatsappMessagesCollection = createCollection(
    queryCollectionOptions<WhatsAppMessage, unknown, any, WhatsAppMessage['id']>({
      id: 'whatsapp_messages',
      queryKey: ['pocketbase', 'whatsapp_messages'],
      queryClient: opts.queryClient,
      getKey: (message) => message.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('whatsapp_messages')
          .getFullList<PbWhatsAppMessageRecord>({ sort: '-updated' })
        return records.map(pbWhatsAppMessageToWhatsAppMessage)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('whatsapp_messages')
            .create<PbWhatsAppMessageRecord>(
              whatsAppMessageToPbCreateInput(mutation.modified),
            )
          requirePbRecord('whatsapp_messages', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('whatsapp_messages')
            .update<PbWhatsAppMessageRecord>(
              expectedId,
              whatsAppMessageToPbInput(mutation.modified),
            )
          requirePbRecord('whatsapp_messages', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('whatsapp_messages').delete(expectedId)
          requirePbDelete('whatsapp_messages', expectedId, result)
        }
      },
    }),
  )

  const appSettingsCollection = createCollection(
    queryCollectionOptions<AppSettings, unknown, any, AppSettings['id']>({
      id: 'app_settings',
      queryKey: ['pocketbase', 'app_settings'],
      queryClient: opts.queryClient,
      getKey: (settings) => settings.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('app_settings')
          .getFullList<PbAppSettingsRecord>()
        return records.map(pbAppSettingsToAppSettings)
      },
      onInsert: async () => {
        throw new Error('app settings is a singleton; it cannot be created here')
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const settings = mutation.modified
          const result = await pb
            .collection('app_settings')
            .update<PbAppSettingsRecord>(expectedId, {
              whatsapp_send_delay_seconds: settings.whatsappSendDelaySeconds,
              whatsapp_sending_enabled: settings.whatsappSendingEnabled,
              reminder_schedule_seconds: settings.reminderScheduleSeconds ?? null,
              reminder_repeat_seconds: settings.reminderRepeatSeconds ?? 0,
              reminder_max_age_days: settings.reminderMaxAgeDays ?? 0,
            })
          requirePbRecord('app_settings', 'update', expectedId, result)
        }
      },
      onDelete: async () => {
        throw new Error('app settings is a singleton; it cannot be deleted here')
      },
    }),
  )

  // Logo files are public (not protected), so their URLs work on the
  // logged-out login/kiosk screens without the file-auth cookie.
  const pbBrandingToBranding = (record: PbBrandingRecord): Branding => {
    const fileUrl = (filename?: string) =>
      filename && typeof pb.files?.getURL === 'function'
        ? pb.files.getURL(record, filename)
        : undefined
    return {
      id: record.id,
      createdAt: normalizePbUtcIso(record.created),
      updatedAt: normalizePbUtcIso(record.updated),
      name: record.name,
      logoUrl: fileUrl(record.logo),
      logoOnLightUrl: fileUrl(record.logo_on_light),
    }
  }

  const brandingCollection = createCollection(
    queryCollectionOptions<Branding, unknown, any, Branding['id']>({
      id: 'branding',
      queryKey: ['pocketbase', 'branding'],
      queryClient: opts.queryClient,
      getKey: (branding) => branding.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('branding')
          .getFullList<PbBrandingRecord>()
        return records.map(pbBrandingToBranding)
      },
      onInsert: async () => {
        throw new Error('branding is a singleton; it cannot be created here')
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const branding = mutation.modified
          // Logos are file uploads; only the name round-trips through the
          // collection for now (an admin Settings page can add uploads later).
          const result = await pb
            .collection('branding')
            .update<PbBrandingRecord>(expectedId, { name: branding.name })
          requirePbRecord('branding', 'update', expectedId, result)
        }
      },
      onDelete: async () => {
        throw new Error('branding is a singleton; it cannot be deleted here')
      },
    }),
  )

  // Admin edit (server rule: @request.auth.is_admin = true). Uploads go as
  // multipart; a removal is a null field, exactly like the profile avatar.
  const saveBranding = async (input: SaveBrandingInput) => {
    await brandingCollection.preload()
    const current = Array.from(brandingCollection.values())[0]
    if (!current) throw new Error('Branding record not found')

    const name = input.name.trim()
    const hasFiles = Boolean(input.logoFile || input.logoOnLightFile)
    let payload: FormData | Record<string, unknown>
    if (hasFiles) {
      const formData = new FormData()
      formData.append('name', name)
      if (input.logoFile) formData.append('logo', input.logoFile)
      else if (input.removeLogo) formData.append('logo', '')
      if (input.logoOnLightFile) formData.append('logo_on_light', input.logoOnLightFile)
      else if (input.removeLogoOnLight) formData.append('logo_on_light', '')
      payload = formData
    } else {
      const data: Record<string, unknown> = { name }
      if (input.removeLogo) data.logo = null
      if (input.removeLogoOnLight) data.logo_on_light = null
      payload = data
    }

    const result = await pb
      .collection('branding')
      .update<PbBrandingRecord>(current.id, payload)
    requirePbRecord('branding', 'update', current.id, result)
    await brandingCollection.utils.refetch()
  }

  // Server-owned: the cron/job hooks create, schedule and delete these. The
  // app only reads them and calls stop/resume (custom routes).
  const customerRemindersCollection = createCollection(
    queryCollectionOptions<
      CustomerReminder,
      unknown,
      any,
      CustomerReminder['id']
    >({
      id: 'customer_reminders',
      queryKey: ['pocketbase', 'customer_reminders'],
      queryClient: opts.queryClient,
      getKey: (reminder) => reminder.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('customer_reminders')
          .getFullList<PbCustomerReminderRecord>({ sort: 'created' })
        return records.map(pbCustomerReminderToReminder)
      },
      onInsert: async () => {
        throw new Error('reminders are created by the server, not the app')
      },
      onUpdate: async () => {
        throw new Error('use stop/resume to change a reminder')
      },
      onDelete: async () => {
        throw new Error('reminders are removed by the server when the last job leaves')
      },
    }),
  )

  async function setCustomerReminderStopped(id: string, stopped: boolean) {
    await pb.send(`/api/customer-reminders/${id}/${stopped ? 'stop' : 'resume'}`, {
      method: 'POST',
      body: {},
      $autoCancel: false,
    })
    await customerRemindersCollection.utils.refetch()
  }
  const stopCustomerReminder = (id: string) => setCustomerReminderStopped(id, true)
  const resumeCustomerReminder = (id: string) => setCustomerReminderStopped(id, false)

  const whatsappTemplatesCollection = createCollection(
    queryCollectionOptions<
      WhatsAppTemplateConfig,
      unknown,
      any,
      WhatsAppTemplateConfig['id']
    >({
      id: 'whatsapp_templates',
      queryKey: ['pocketbase', 'whatsapp_templates'],
      queryClient: opts.queryClient,
      getKey: (template) => template.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('whatsapp_templates')
          .getFullList<PbWhatsAppTemplateRecord>({ sort: 'key' })
        return records.map(pbWhatsAppTemplateToConfig)
      },
      onInsert: async () => {
        throw new Error('template purposes are a fixed set; they cannot be created here')
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const template = mutation.modified
          const result = await pb
            .collection('whatsapp_templates')
            .update<PbWhatsAppTemplateRecord>(expectedId, {
              content_sid: template.contentSid ?? '',
              variables: template.variables ?? {},
            })
          requirePbRecord('whatsapp_templates', 'update', expectedId, result)
        }
      },
      onDelete: async () => {
        throw new Error('template purposes are a fixed set; they cannot be deleted here')
      },
    }),
  )

  const whatsappTwilioTemplatesCollection = createCollection(
    queryCollectionOptions<
      WhatsAppTwilioTemplate,
      unknown,
      any,
      WhatsAppTwilioTemplate['id']
    >({
      id: 'whatsapp_twilio_templates',
      queryKey: ['pocketbase', 'whatsapp_twilio_templates'],
      queryClient: opts.queryClient,
      getKey: (template) => template.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('whatsapp_twilio_templates')
          .getFullList<PbWhatsAppTwilioTemplateRecord>({ sort: 'friendly_name' })
        return records.map(pbWhatsAppTwilioTemplateToTemplate)
      },
      onInsert: async () => {
        throw new Error('twilio templates are synced from Twilio, not created here')
      },
      onUpdate: async () => {
        throw new Error('twilio templates are synced from Twilio, not edited here')
      },
      onDelete: async () => {
        throw new Error('twilio templates are synced from Twilio, not deleted here')
      },
    }),
  )

  const jobsCollection = createCollection(
    queryCollectionOptions<Job, unknown, any, Job['id']>({
      id: 'jobs',
      queryKey: ['pocketbase', 'jobs'],
      queryClient: opts.queryClient,
      getKey: (job) => job.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('jobs')
          .getFullList<PbJobRecord>({
            sort: '-updated',
            filter: keepingHydrated(activeServiceFilter, 'job'),
          })
        return records.map(pbJobToJob)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb.collection('jobs').create<PbJobRecord>(jobToPbCreateInput(mutation.modified))
          requirePbRecord('jobs', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const payload = jobToPbInput(mutation.modified)
          try {
            const result = await pb.collection('jobs').update<PbJobRecord>(expectedId, payload)
            requirePbRecord('jobs', 'update', expectedId, result)
          } catch (error) {
            console.error('PocketBase jobs.update failed', { expectedId, payload, error })
            throw error
          }
        }
        // The server hook appended a revision inside that update; pull it in
        // so the trail card updates without a reload.
        void refetchServiceRevisions('job')
        // An amended archived job must not leave its ledger row stale.
        reconcileArchivedRows(
          'job',
          transaction.mutations.map((mutation) => mutation.modified),
        )
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('jobs').delete(expectedId)
          requirePbDelete('jobs', expectedId, result)
        }
      },
    }),
  )

  const kiosksCollection = createCollection(
    queryCollectionOptions<Kiosk, unknown, any, Kiosk['id']>({
      id: 'kiosks',
      queryKey: ['pocketbase', 'kiosks'],
      queryClient: opts.queryClient,
      getKey: (kiosk) => kiosk.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('kiosks')
          .getFullList<PbKioskRecord>({ sort: 'label' })
        return records.map(pbKioskToKiosk)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb.collection('kiosks').create<PbKioskRecord>(
            kioskToPbCreateInput(mutation.modified),
          )
          requirePbRecord('kiosks', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('kiosks')
            .update<PbKioskRecord>(expectedId, kioskToPbInput(mutation.modified))
          requirePbRecord('kiosks', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('kiosks').delete(expectedId)
          requirePbDelete('kiosks', expectedId, result)
        }
      },
    }),
  )

  const kioskSessionsCollection = createCollection(
    queryCollectionOptions<KioskSession, unknown, any, KioskSession['id']>({
      id: 'kiosk_sessions',
      queryKey: ['pocketbase', 'kiosk_sessions'],
      queryClient: opts.queryClient,
      getKey: (session) => session.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('kiosk_sessions')
          .getFullList<PbKioskSessionRecord>({ sort: 'created' })
        return records.map(pbKioskSessionToKioskSession)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb.collection('kiosk_sessions').create<PbKioskSessionRecord>(
            kioskSessionToPbCreateInput(mutation.modified),
            // requestKey: null — see onUpdate; create shares one default cancel
            // key for the whole collection (no id in the path).
            { requestKey: null },
          )
          requirePbRecord('kiosk_sessions', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          // requestKey: null — kiosk form patches hit the same session record
          // in quick succession (debounced typing flush, step changes), and the
          // SDK's default per-path auto-cancellation would abort the in-flight
          // read/update when the next one starts, failing the earlier
          // transaction with "autocancelled" (seen in Sentry).
          const currentRecord = pbKioskSessionToKioskSession(
            await pb
              .collection('kiosk_sessions')
              .getOne<PbKioskSessionRecord>(expectedId, { requestKey: null }),
          )
          const nextSession: KioskSession = {
            ...currentRecord,
            ...mutation.modified,
            currentStep:
              typeof mutation.modified.currentStep === 'number' && Number.isFinite(mutation.modified.currentStep)
                ? mutation.modified.currentStep
                : currentRecord.currentStep,
            formData: mutation.modified.formData ?? currentRecord.formData ?? {},
            jobId:
              Object.prototype.hasOwnProperty.call(mutation.modified, 'jobId')
                ? mutation.modified.jobId
                : currentRecord.jobId,
          }
          const payload = kioskSessionToPbInput(nextSession)
          const result = await pb
            .collection('kiosk_sessions')
            .update<PbKioskSessionRecord>(
              expectedId,
              payload,
              { requestKey: null },
            )
          requirePbRecord('kiosk_sessions', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('kiosk_sessions').delete(expectedId)
          requirePbDelete('kiosk_sessions', expectedId, result)
        }
      },
    }),
  )

  const documentsCollection = createCollection(
    queryCollectionOptions<Document, unknown, any, Document['id']>({
      id: 'documents',
      queryKey: ['pocketbase', 'documents'],
      queryClient: opts.queryClient,
      getKey: (document) => document.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('documents')
          .getFullList<PbDocumentRecord>({ sort: 'title' })
        return records.map(pbDocumentToDocument)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb.collection('documents').create<PbDocumentRecord>(
            documentToPbCreateInput(mutation.modified),
          )
          requirePbRecord('documents', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('documents')
            .update<PbDocumentRecord>(expectedId, documentToPbInput(mutation.modified))
          requirePbRecord('documents', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('documents').delete(expectedId)
          requirePbDelete('documents', expectedId, result)
        }
      },
    }),
  )

  const kioskPairedDevicesCollection = createCollection(
    queryCollectionOptions<KioskPairedDevice, unknown, any, KioskPairedDevice['id']>({
      id: 'kiosk_paired_devices',
      queryKey: ['pocketbase', 'kiosk_paired_devices'],
      queryClient: opts.queryClient,
      getKey: (device) => device.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('kiosk_tablet_users')
          .getFullList<PbKioskTabletUserDeviceRecord>({ sort: '-last_seen_at,-paired_at,-created' })
        return records
          .map(pbKioskTabletUserToKioskPairedDevice)
          .filter((record): record is KioskPairedDevice => Boolean(record))
      },
      onInsert: async () => {
        throw new Error('Direct kiosk paired-device inserts are not supported; use the kiosk pairing flow.')
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('kiosk_tablet_users')
            .update<PbKioskTabletUserDeviceRecord>(expectedId, kioskPairedDeviceToPbTabletUserInput(mutation.modified))
          requirePbRecord('kiosk_tablet_users', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('kiosk_tablet_users').delete(expectedId)
          requirePbDelete('kiosk_tablet_users', expectedId, result)
        }
      },
    }),
  )

  const calloutsCollection = createCollection(
    queryCollectionOptions<Callout, unknown, any, Callout['id']>({
      id: 'callouts',
      queryKey: ['pocketbase', 'callouts'],
      queryClient: opts.queryClient,
      getKey: (callout) => callout.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('callouts')
          .getFullList<PbCalloutRecord>({
            sort: '-updated',
            filter: keepingHydrated(activeServiceFilter, 'callout'),
          })
        return records.map(pbCalloutToCallout)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('callouts')
            .create<PbCalloutRecord>(calloutToPbCreateInput(mutation.modified))

          requirePbRecord('callouts', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('callouts')
            .update<PbCalloutRecord>(expectedId, calloutToPbInput(mutation.modified))
          requirePbRecord('callouts', 'update', expectedId, result)
        }
        // The server hook appended a revision inside that update; pull it in
        // so the trail card updates without a reload.
        void refetchServiceRevisions('callout')
        // An amended archived callout must not leave its ledger row stale.
        reconcileArchivedRows(
          'callout',
          transaction.mutations.map((mutation) => mutation.modified),
        )
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('callouts').delete(expectedId)
          requirePbDelete('callouts', expectedId, result)
        }
      },
    }),
  )

  const sessionsCollection = createCollection(
    queryCollectionOptions<Callout, unknown, any, Callout['id']>({
      id: 'sessions',
      queryKey: ['pocketbase', 'sessions'],
      queryClient: opts.queryClient,
      getKey: (session) => session.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('sessions')
          .getFullList<PbCalloutRecord>({
            sort: '-updated',
            filter: keepingHydrated(activeServiceFilter, 'session'),
          })
        return records.map(pbCalloutToCallout)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('sessions')
            .create<PbCalloutRecord>(calloutToPbCreateInput(mutation.modified))

          requirePbRecord('sessions', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('sessions')
            .update<PbCalloutRecord>(expectedId, calloutToPbInput(mutation.modified))
          requirePbRecord('sessions', 'update', expectedId, result)
        }
        // The server hook appended a revision inside that update; pull it in
        // so the trail card updates without a reload.
        void refetchServiceRevisions('session')
        // An amended archived session must not leave its ledger row stale.
        reconcileArchivedRows(
          'session',
          transaction.mutations.map((mutation) => mutation.modified),
        )
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('sessions').delete(expectedId)
          requirePbDelete('sessions', expectedId, result)
        }
      },
    }),
  )

  /**
   * The one funnel through which fetched service records land in memory.
   *
   * Jobs, callouts and sessions each have exactly one in-memory copy — the
   * entry in their collection — and every path that fetches records outside
   * the collection's own sync (hydration, realtime completions, fresh reads
   * for composing) must land them here rather than keeping a loose copy.
   * Result sets hold ids only; screens join ids back against the collections.
   *
   * `keepAcrossRefetch` marks records the active-only sync filters would
   * otherwise evict on the next refetch (archived services hydrated for a
   * detail page). Leave it off for records known to be in the active slice —
   * remembering them would push genuinely archived ids out of the bounded
   * keep-list.
   */
  async function upsertServiceRecords(
    type: HydratedServiceType,
    records: RecordModel[],
    opts?: { keepAcrossRefetch?: boolean },
  ): Promise<void> {
    if (records.length === 0) return
    if (opts?.keepAcrossRefetch) {
      // Remember before any write: a collection that starts syncing from here
      // on picks up the widened filter and fetches these records' children.
      for (const record of records) rememberHydratedService(type, record.id)
    }
    if (type === 'job') {
      const jobs = records.map((record) =>
        pbJobToJob(record as unknown as PbJobRecord),
      )
      reconcileArchivedRows('job', jobs)
      await upsertWhenReady(jobsCollection, jobs)
      return
    }
    const callouts = records.map((record) =>
      pbCalloutToCallout(record as unknown as PbCalloutRecord),
    )
    reconcileArchivedRows(type, callouts)
    await upsertWhenReady(
      type === 'callout' ? calloutsCollection : sessionsCollection,
      callouts,
    )
  }

  /**
   * Keeps the archived-services rows honest against the record cache.
   *
   * The rows are a projection of the server-side view, cached locally for the
   * ledger; without this, amending an archived service leaves its row showing
   * the old summary/status until the next search. Fields the view denormalises
   * from elsewhere (customer and assignee names) are left alone. A record
   * whose status is no longer archived loses its row — it belongs to the
   * active ledger now.
   */
  function reconcileArchivedRows(
    type: HydratedServiceType,
    records: Array<Job | Callout>,
  ): void {
    const byRecordId = new Map(records.map((record) => [record.id, record]))
    for (const row of archivedServicesCollection.toArray) {
      const record = byRecordId.get(row.recordId)
      if (!record) continue
      const rowType: HydratedServiceType =
        row.type === 'job' ? 'job' : row.type === 'callout' ? 'callout' : 'session'
      if (rowType !== type) continue

      if (record.status !== 'Invoiced' && record.status !== 'Cancelled') {
        archivedServicesCollection.delete(row.key)
        continue
      }
      archivedServicesCollection.update(row.key, (draft) => {
        draft.status = record.status
        draft.summary =
          type === 'job' ? (record as Job).fault : (record as Callout).issueSummary
        draft.updatedAt = record.updatedAt
      })
    }
  }

  /**
   * Concurrent hydrations of the same record share one request. Two screens
   * can mount on the same missing service at once (a detail page and a
   * search result landing the same id, say); without this they would both getOne and
   * race their writes into the collection.
   */
  const inflightHydrations = new Map<string, Promise<boolean>>()

  function singleFlight(
    key: string,
    run: () => Promise<boolean>,
  ): Promise<boolean> {
    const existing = inflightHydrations.get(key)
    if (existing) return existing
    const promise = run().finally(() => {
      inflightHydrations.delete(key)
    })
    inflightHydrations.set(key, promise)
    return promise
  }

  const notesCollection = createCollection(
    queryCollectionOptions<Note, unknown, any, Note['id']>({
      id: 'callout_notes',
      queryKey: ['pocketbase', 'callout_notes'],
      queryClient: opts.queryClient,
      getKey: (note) => note.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('callout_notes')
          .getFullList<PbCalloutNoteRecord>({
            sort: '-updated',
            filter: keepingHydrated(activeParentFilter('callout_id'), 'callout', 'callout_id'),
          })
        return records.map((record) => pbNoteToNote(record, pb, 'callout_id'))
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('callout_notes')
            .create<PbCalloutNoteRecord>(noteToPbCreateInput(mutation.modified, 'callout_id'))
          requirePbRecord('callout_notes', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('callout_notes')
            .update<PbCalloutNoteRecord>(expectedId, noteToPbInput(mutation.modified, 'callout_id'))
          requirePbRecord('callout_notes', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('callout_notes').delete(expectedId)
          requirePbDelete('callout_notes', expectedId, result)
        }
      },
    }),
  )

  const sessionNotesCollection = createCollection(
    queryCollectionOptions<Note, unknown, any, Note['id']>({
      id: 'session_notes',
      queryKey: ['pocketbase', 'session_notes'],
      queryClient: opts.queryClient,
      getKey: (note) => note.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('session_notes')
          .getFullList<PbSessionNoteRecord>({
            sort: '-updated',
            filter: keepingHydrated(activeParentFilter('session_id'), 'session', 'session_id'),
          })
        return records.map((record) => pbNoteToNote(record, pb, 'session_id'))
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('session_notes')
            .create<PbSessionNoteRecord>(
              noteToPbCreateInput(mutation.modified, 'session_id'),
            )
          requirePbRecord('session_notes', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('session_notes')
            .update<PbSessionNoteRecord>(
              expectedId,
              noteToPbInput(mutation.modified, 'session_id'),
            )
          requirePbRecord('session_notes', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('session_notes').delete(expectedId)
          requirePbDelete('session_notes', expectedId, result)
        }
      },
    }),
  )

  const jobNotesCollection = createCollection(
    queryCollectionOptions<Note, unknown, any, Note['id']>({
      id: 'job_notes',
      queryKey: ['pocketbase', 'job_notes'],
      queryClient: opts.queryClient,
      getKey: (note) => note.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('job_notes')
          .getFullList<PbJobNoteRecord>({
            sort: '-updated',
            filter: keepingHydrated(activeParentFilter('job_id'), 'job', 'job_id'),
          })
        return records.map((record) => pbNoteToNote(record, pb, 'job_id'))
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('job_notes')
            .create<PbJobNoteRecord>(
              noteToPbCreateInput(mutation.modified, 'job_id'),
            )
          requirePbRecord('job_notes', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('job_notes')
            .update<PbJobNoteRecord>(
              expectedId,
              noteToPbInput(mutation.modified, 'job_id'),
            )
          requirePbRecord('job_notes', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('job_notes').delete(expectedId)
          requirePbDelete('job_notes', expectedId, result)
        }
      },
    }),
  )

  // Unlike the service note collections, these two sync unfiltered. There is no
  // parent status to exclude on — a customer is never locked or archived — and
  // the volumes are small enough (a shop's follow-ups and customer notes, not
  // per-service logs) that partial sync would cost more than it saves.
  const customerNotesCollection = createCollection(
    queryCollectionOptions<CustomerNote, unknown, any, CustomerNote['id']>({
      id: 'customer_notes',
      queryKey: ['pocketbase', 'customer_notes'],
      queryClient: opts.queryClient,
      getKey: (note) => note.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('customer_notes')
          .getFullList<PbCustomerNoteRecord>({ sort: '-updated' })
        return records.map((record) => pbCustomerNoteToCustomerNote(record, pb))
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('customer_notes')
            .create<PbCustomerNoteRecord>(
              customerNoteToPbCreateInput(mutation.modified),
            )
          requirePbRecord('customer_notes', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('customer_notes')
            .update<PbCustomerNoteRecord>(
              expectedId,
              customerNoteToPbInput(mutation.modified),
            )
          requirePbRecord('customer_notes', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('customer_notes').delete(expectedId)
          requirePbDelete('customer_notes', expectedId, result)
        }
      },
    }),
  )

  const customerDocumentsCollection = createCollection(
    queryCollectionOptions<
      CustomerDocumentWithFile,
      unknown,
      any,
      CustomerDocument['id']
    >({
      id: 'customer_documents',
      queryKey: ['pocketbase', 'customer_documents'],
      queryClient: opts.queryClient,
      getKey: (document) => document.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('customer_documents')
          .getFullList<PbCustomerDocumentRecord>({ sort: '-created' })
        return records.map((record) =>
          pbCustomerDocumentToCustomerDocument(record, pb),
        )
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('customer_documents')
            .create<PbCustomerDocumentRecord>(
              customerDocumentToPbCreateInput(mutation.modified),
            )
          requirePbRecord('customer_documents', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('customer_documents')
            .update<PbCustomerDocumentRecord>(
              expectedId,
              customerDocumentToPbInput(mutation.modified),
            )
          requirePbRecord('customer_documents', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('customer_documents')
            .delete(expectedId)
          requirePbDelete('customer_documents', expectedId, result)
        }
      },
    }),
  )

  const followUpsCollection = createCollection(
    queryCollectionOptions<FollowUp, unknown, any, FollowUp['id']>({
      id: 'follow_ups',
      queryKey: ['pocketbase', 'follow_ups'],
      queryClient: opts.queryClient,
      getKey: (followUp) => followUp.id,
      startSync: false,
      queryFn: async () => {
        // Only the actionable slice syncs: open follow-ups already due, plus a
        // day of buffer so the badge tick can cross midnight without a refetch.
        // The unbounded rest (upcoming and closed) loads page-by-page through
        // searchFollowUps, and per-customer via loadCustomerFollowUps.
        const records = await pb
          .collection('follow_ups')
          .getFullList<PbFollowUpRecord>({
            sort: 'due_at',
            filter: pb.filter("status = 'open' && due_at < {:horizon}", {
              horizon: new Date(dueSyncHorizonUtcIso()),
            }),
          })
        return records.map(pbFollowUpToFollowUp)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('follow_ups')
            .create<PbFollowUpRecord>(followUpToPbCreateInput(mutation.modified))
          requirePbRecord('follow_ups', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb
            .collection('follow_ups')
            .update<PbFollowUpRecord>(
              expectedId,
              followUpToPbInput(mutation.modified),
            )
          requirePbRecord('follow_ups', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.key
          const result = await pb.collection('follow_ups').delete(expectedId)
          requirePbDelete('follow_ups', expectedId, result)
        }
      },
    }),
  )

  const statusChangesCollection = createCollection(
    queryCollectionOptions<StatusChangeLog, unknown, any, StatusChangeLog['id']>({
      id: 'callout_status_changes',
      queryKey: ['pocketbase', 'callout_status_changes'],
      queryClient: opts.queryClient,
      getKey: (statusChange) => statusChange.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('callout_status_changes')
          .getFullList<PbCalloutStatusChangeRecord>({
            sort: '-created',
            filter: keepingHydrated(activeParentFilter('callout_id'), 'callout', 'callout_id'),
          })
        return records.map((record) =>
          pbStatusChangeToStatusChangeLog(record, 'callout_id'),
        )
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('callout_status_changes')
            .create<PbCalloutStatusChangeRecord>(
              statusChangeLogToPbCreateInput(mutation.modified, 'callout_id'),
            )
          requirePbRecord('callout_status_changes', 'create', expectedId, result)
        }
      },
      onUpdate: async () => {
        throw new Error('callout_status_changes are immutable and cannot be updated')
      },
      onDelete: async () => {
        throw new Error('callout_status_changes are immutable and cannot be deleted')
      },
    }),
  )

  const sessionStatusChangesCollection = createCollection(
    queryCollectionOptions<StatusChangeLog, unknown, any, StatusChangeLog['id']>({
      id: 'session_status_changes',
      queryKey: ['pocketbase', 'session_status_changes'],
      queryClient: opts.queryClient,
      getKey: (statusChange) => statusChange.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('session_status_changes')
          .getFullList<PbSessionStatusChangeRecord>({
            sort: '-created',
            filter: keepingHydrated(activeParentFilter('session_id'), 'session', 'session_id'),
          })
        return records.map((record) =>
          pbStatusChangeToStatusChangeLog(record, 'session_id'),
        )
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const result = await pb
            .collection('session_status_changes')
            .create<PbSessionStatusChangeRecord>(
              statusChangeLogToPbCreateInput(mutation.modified, 'session_id'),
            )
          requirePbRecord('session_status_changes', 'create', expectedId, result)
        }
      },
      onUpdate: async () => {
        throw new Error('session_status_changes are immutable and cannot be updated')
      },
      onDelete: async () => {
        throw new Error('session_status_changes are immutable and cannot be deleted')
      },
    }),
  )

  const jobStatusChangesCollection = createCollection(
    queryCollectionOptions<StatusChangeLog, unknown, any, StatusChangeLog['id']>({
      id: 'job_status_changes',
      queryKey: ['pocketbase', 'job_status_changes'],
      queryClient: opts.queryClient,
      getKey: (statusChange) => statusChange.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('job_status_changes')
          .getFullList<PbJobStatusChangeRecord>({
            sort: '-created',
            filter: keepingHydrated(activeParentFilter('job_id'), 'job', 'job_id'),
          })
        return records.map((record) =>
          pbStatusChangeToStatusChangeLog(record, 'job_id'),
        )
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const payload = statusChangeLogToPbCreateInput(mutation.modified, 'job_id')
          try {
            const result = await pb
              .collection('job_status_changes')
              .create<PbJobStatusChangeRecord>(payload)
            requirePbRecord('job_status_changes', 'create', expectedId, result)
          } catch (error) {
            console.error('PocketBase job_status_changes.create failed', { expectedId, payload, error })
            throw error
          }
        }
      },
      onUpdate: async () => {
        throw new Error('job_status_changes are immutable and cannot be updated')
      },
      onDelete: async () => {
        throw new Error('job_status_changes are immutable and cannot be deleted')
      },
    }),
  )

  // Addendums deliberately skip `activeParentFilter`: they only ever exist on
  // Invoiced/Cancelled services, which that filter is designed to exclude.
  const createAddendumsCollection = (
    relationField: 'callout_id' | 'session_id' | 'job_id',
  ) => {
    const collectionName =
      relationField === 'callout_id'
        ? 'callout_addendums'
        : relationField === 'session_id'
          ? 'session_addendums'
          : 'job_addendums'

    return createCollection(
      queryCollectionOptions<Addendum, unknown, any, Addendum['id']>({
        id: collectionName,
        queryKey: ['pocketbase', collectionName],
        queryClient: opts.queryClient,
        getKey: (addendum) => addendum.id,
        startSync: false,
        queryFn: async () => {
          const records = await pb
            .collection(collectionName)
            .getFullList<PbAddendumRecord>({ sort: '-created' })
          return records.map((record) =>
            pbAddendumToAddendum(record, relationField),
          )
        },
        onInsert: async ({ transaction }) => {
          for (const mutation of transaction.mutations) {
            const expectedId = mutation.modified.id
            const payload = addendumToPbCreateInput(
              mutation.modified,
              relationField,
            )
            try {
              const result = await pb
                .collection(collectionName)
                .create<PbAddendumRecord>(payload)
              requirePbRecord(collectionName, 'create', expectedId, result)
            } catch (error) {
              console.error(`PocketBase ${collectionName}.create failed`, {
                expectedId,
                payload,
                error,
              })
              throw error
            }
          }
        },
        onUpdate: async () => {
          throw new Error(`${collectionName} are immutable and cannot be updated`)
        },
        onDelete: async () => {
          throw new Error(`${collectionName} are immutable and cannot be deleted`)
        },
      }),
    )
  }

  const addendumsCollection = createAddendumsCollection('callout_id')
  const sessionAddendumsCollection = createAddendumsCollection('session_id')
  const jobAddendumsCollection = createAddendumsCollection('job_id')

  // Server-authored only: the service-revisions hook appends these inside the
  // service update request, so the client never creates one — inserting here
  // is a programming error, not an idempotent no-op like customer_revisions'.
  const createServiceRevisionsCollection = (
    relationField: 'callout_id' | 'session_id' | 'job_id',
  ) => {
    const collectionName =
      relationField === 'callout_id'
        ? 'callout_revisions'
        : relationField === 'session_id'
          ? 'session_revisions'
          : 'job_revisions'

    return createCollection(
      queryCollectionOptions<ServiceRevision, unknown, any, ServiceRevision['id']>({
        id: collectionName,
        queryKey: ['pocketbase', collectionName],
        queryClient: opts.queryClient,
        getKey: (revision) => revision.id,
        startSync: false,
        queryFn: async () => {
          const records = await pb
            .collection(collectionName)
            .getFullList<PbServiceRevisionRecord>({ sort: '-created' })
          return records.map((record) =>
            pbServiceRevisionToServiceRevision(record, relationField),
          )
        },
        onInsert: async () => {
          throw new Error(`${collectionName} are server-authored and cannot be created by the client`)
        },
        onUpdate: async () => {
          throw new Error(`${collectionName} are immutable and cannot be updated`)
        },
        onDelete: async () => {
          throw new Error(`${collectionName} are immutable and cannot be deleted`)
        },
      }),
    )
  }

  const calloutRevisionsCollection = createServiceRevisionsCollection('callout_id')
  const sessionRevisionsCollection = createServiceRevisionsCollection('session_id')
  const jobRevisionsCollection = createServiceRevisionsCollection('job_id')

  const refetchServiceRevisions = async (
    type: 'callout' | 'session' | 'job',
  ) => {
    const target =
      type === 'callout'
        ? calloutRevisionsCollection
        : type === 'session'
          ? sessionRevisionsCollection
          : jobRevisionsCollection
    // Not ready = nothing subscribed yet; the queryFn will fetch fresh rows
    // whenever the trail is first shown, so there is nothing to refresh.
    if (target.isReady()) await target.utils.refetch()
  }

  // Like customer_notes, revisions sync unfiltered: a customer is never
  // locked or archived, and the volume is one small record per details edit.
  const customerRevisionsCollection = createCollection(
    queryCollectionOptions<CustomerRevision, unknown, any, CustomerRevision['id']>({
      id: 'customer_revisions',
      queryKey: ['pocketbase', 'customer_revisions'],
      queryClient: opts.queryClient,
      getKey: (revision) => revision.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('customer_revisions')
          .getFullList<PbCustomerRevisionRecord>({ sort: '-created' })
        return records.map(pbCustomerRevisionToCustomerRevision)
      },
      // Deliberately does NOT create a PB record: the customer-revisions
      // server hook is the authority and appends the durable revision inside
      // the customers update request itself, so no API path can skip it. The
      // command's insert lands here purely as the optimistic display row; the
      // next refetch reconciles it away in favour of the server-authored one
      // (same content, server id).
      onInsert: async () => {},
      onUpdate: async () => {
        throw new Error('customer_revisions are immutable and cannot be updated')
      },
      onDelete: async () => {
        throw new Error('customer_revisions are immutable and cannot be deleted')
      },
    }),
  )

  const jobAssignmentsCollection = createCollection(
    queryCollectionOptions<JobAssignment, unknown, any, JobAssignment['id']>({
      id: 'job_assignments',
      queryKey: ['pocketbase', 'job_assignments'],
      queryClient: opts.queryClient,
      getKey: (assignment) => assignment.id,
      startSync: false,
      queryFn: async () => {
        const records = await pb
          .collection('job_assignments')
          .getFullList<PbJobAssignmentRecord>({
            sort: '-created',
            filter: keepingHydrated(activeParentFilter('job_id'), 'job', 'job_id'),
          })
        return records.map(pbJobAssignmentToJobAssignment)
      },
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const payload = jobAssignmentToPbCreateInput(mutation.modified)
          const result = await pb
            .collection('job_assignments')
            .create<PbJobAssignmentRecord>(payload)
          requirePbRecord('job_assignments', 'create', expectedId, result)
        }
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const expectedId = mutation.modified.id
          const payload = jobAssignmentToPbInput(mutation.modified)
          const result = await pb
            .collection('job_assignments')
            .update<PbJobAssignmentRecord>(expectedId, payload)
          requirePbRecord('job_assignments', 'update', expectedId, result)
        }
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          await pb.collection('job_assignments').delete(mutation.original.id)
        }
      },
    }),
  )

  const makeServiceAssignmentsCollection = (
    collectionName: 'callout_assignments' | 'session_assignments',
    relationField: 'callout_id' | 'session_id',
  ) =>
    createCollection(
      queryCollectionOptions<ServiceAssignment, unknown, any, ServiceAssignment['id']>({
        id: collectionName,
        queryKey: ['pocketbase', collectionName],
        queryClient: opts.queryClient,
        getKey: (assignment) => assignment.id,
        startSync: false,
        queryFn: async () => {
          const records = await pb
            .collection(collectionName)
            .getFullList<PbServiceAssignmentRecord>({
              sort: '-created',
              filter: keepingHydrated(
                activeParentFilter(relationField),
                relationField === 'callout_id' ? 'callout' : 'session',
                relationField,
              ),
            })
          return records.map((record) =>
            pbServiceAssignmentToServiceAssignment(record, relationField),
          )
        },
        onInsert: async ({ transaction }) => {
          for (const mutation of transaction.mutations) {
            const expectedId = mutation.modified.id
            const payload = {
              id: mutation.modified.id,
              ...serviceAssignmentToPbInput(mutation.modified, relationField),
            }
            const result = await pb
              .collection(collectionName)
              .create<PbServiceAssignmentRecord>(payload)
            requirePbRecord(collectionName, 'create', expectedId, result)
          }
        },
        onUpdate: async ({ transaction }) => {
          for (const mutation of transaction.mutations) {
            const expectedId = mutation.modified.id
            const payload = serviceAssignmentToPbInput(mutation.modified, relationField)
            const result = await pb
              .collection(collectionName)
              .update<PbServiceAssignmentRecord>(expectedId, payload)
            requirePbRecord(collectionName, 'update', expectedId, result)
          }
        },
        onDelete: async ({ transaction }) => {
          for (const mutation of transaction.mutations) {
            await pb.collection(collectionName).delete(mutation.original.id)
          }
        },
      }),
    )

  const calloutAssignmentsCollection = makeServiceAssignmentsCollection(
    'callout_assignments',
    'callout_id',
  )
  const sessionAssignmentsCollection = makeServiceAssignmentsCollection(
    'session_assignments',
    'session_id',
  )

  const usersLookup: UserLookupPort = {
    getById(id) {
      return (usersCollection.get?.(id) as User | undefined) ?? opts.seedUsers.find((u) => u.id === id)
    },
  }

  // The archived_services view infers every column as a json field, so values
  // arrive untyped (strings, or 0/1 for booleans) — coerce defensively.
  const archivedSortFields: Record<ArchivedServiceSortKey, string> = {
    ref: 'public_id',
    type: 'type',
    customer: 'customer_name',
    issue: 'summary',
    booked: 'booked_at',
    assignee: 'assignee_name',
    status: 'status',
    updated: 'updated',
  }

  function pbArchivedServiceToRow(record: Record<string, unknown>): ArchivedServiceRow {
    const str = (value: unknown) => (value == null ? '' : String(value))
    return {
      key: str(record.id),
      type: str(record.type) as ArchivedServiceType,
      recordId: str(record.record_id),
      publicId: str(record.public_id),
      customerId: str(record.customer_id) || undefined,
      customerPublicId: str(record.customer_public_id) || undefined,
      customerName: str(record.customer_name),
      summary: str(record.summary),
      bookedAt: normalizePbUtcIso(str(record.booked_at)),
      isWholeDay: record.is_whole_day === true || record.is_whole_day === 1,
      assigneeName: str(record.assignee_name) || undefined,
      status: str(record.status) as ServiceStatus,
      updatedAt: normalizePbUtcIso(str(record.updated)),
    }
  }

  // Archived-search rows are projections of the server-side view, cached
  // locally so the UI reads them through TanStack DB. Archived services are
  // Invoiced/Cancelled and effectively frozen, so staleness is a non-issue.
  const archivedServicesCollection = createCollection(
    localOnlyCollectionOptions<ArchivedServiceRow, ArchivedServiceRow['key']>({
      id: 'archived-service-rows',
      getKey: (row) => row.key,
    }),
  )

  function upsertArchivedRow(row: ArchivedServiceRow) {
    if (archivedServicesCollection.has(row.key)) {
      archivedServicesCollection.update(row.key, (draft) => {
        Object.assign(draft, row)
      })
    } else {
      archivedServicesCollection.insert(row)
    }
  }

  async function searchArchivedServices(
    input: ArchivedServicesSearchInput,
  ): Promise<ArchivedServicesSearchResult> {
    const filters = input.filters ?? {}
    const parts: string[] = []
    const params: Record<string, string> = {}

    const addContains = (field: string, key: string, value?: string) => {
      if (!value?.trim()) return
      parts.push(`${field} ~ {:${key}}`)
      params[key] = value.trim()
    }
    // Select filters may carry several comma-joined values (multi-select in
    // the ledger's filter row) — OR them together, matching the client-side
    // filter semantics.
    const addEquals = (field: string, key: string, value?: string) => {
      const tokens = (value ?? '')
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean)
      if (tokens.length === 0) return
      const clause = tokens
        .map((token, index) => {
          const paramKey = index === 0 ? key : `${key}${index}`
          params[paramKey] = token
          return `${field} = {:${paramKey}}`
        })
        .join(' || ')
      parts.push(tokens.length === 1 ? clause : `(${clause})`)
    }

    addContains('public_id', 'ref', filters.ref)
    addEquals('type', 'type', filters.type)
    addContains('customer_name', 'customer', filters.customer)
    addEquals('customer_id', 'customerId', filters.customerId)
    addContains('summary', 'issue', filters.issue)
    addContains('assignee_name', 'assignee', filters.assignee)
    addEquals('status', 'status', filters.status)

    const sort = input.sort ?? { key: 'updated' as const, dir: 'desc' as const }
    const result = await pb
      .collection('archived_services')
      .getList<Record<string, unknown>>(input.page, input.pageSize, {
        filter: parts.length > 0 ? pb.filter(parts.join(' && '), params) : '',
        sort: `${sort.dir === 'desc' ? '-' : ''}${archivedSortFields[sort.key]}`,
      })

    const rows = result.items.map(pbArchivedServiceToRow)
    for (const row of rows) upsertArchivedRow(row)

    return {
      keys: rows.map((row) => row.key),
      page: result.page,
      pageSize: result.perPage,
      totalItems: result.totalItems,
      totalPages: result.totalPages,
    }
  }

  const customerSortFields: Record<CustomerSearchSortKey, string> = {
    ref: 'public_id',
    customer: 'full_name',
    invoiceTo: 'invoice_name',
    updated: 'updated',
  }

  async function searchCustomers(
    input: CustomersSearchInput,
  ): Promise<CustomersSearchResult> {
    const filters = input.filters ?? {}
    const parts: string[] = [
      input.scope === 'archived' ? "archived_at != ''" : "archived_at = ''",
    ]
    const params: Record<string, string> = {}

    const addContains = (fields: string[], key: string, value?: string) => {
      if (!value?.trim()) return
      parts.push(
        `(${fields.map((field) => `${field} ~ {:${key}}`).join(' || ')})`,
      )
      params[key] = value.trim()
    }

    addContains(['public_id'], 'ref', filters.ref)
    addContains(['full_name', 'external_id'], 'customer', filters.customer)
    addContains(['contact_email', 'contact_phone'], 'contact', filters.contact)
    addContains(['contact_address'], 'address', filters.address)
    addContains(['invoice_name'], 'invoiceTo', filters.invoiceTo)

    const sort = input.sort ?? { key: 'updated' as const, dir: 'desc' as const }
    const result = await pb
      .collection('customers')
      .getList<PbCustomerRecord>(input.page, input.pageSize, {
        filter: parts.length > 0 ? pb.filter(parts.join(' && '), params) : '',
        sort: `${sort.dir === 'desc' ? '-' : ''}${customerSortFields[sort.key]}`,
      })

    const customers = result.items.map(pbCustomerToCustomer)
    await upsertWhenReady(customersCollection, customers)

    return {
      keys: customers.map((customer) => customer.id),
      page: result.page,
      pageSize: result.perPage,
      totalItems: result.totalItems,
      totalPages: result.totalPages,
    }
  }

  // Archiving spans many collections in one server transaction, so it goes
  // straight to the backend; realtime events refresh the affected slices.
  // The two customers involved are refetched explicitly so the screen that
  // triggered the action updates without waiting for the stream.
  async function refreshCustomer(id: string) {
    try {
      const record = await pb.collection('customers').getOne<PbCustomerRecord>(id)
      await upsertWhenReady(customersCollection, pbCustomerToCustomer(record))
    } catch {
      // Realtime/next refetch will settle it.
    }
  }

  async function previewCustomerArchive(customerId: string): Promise<CustomerArchivePreview> {
    return (await pb.send(`/api/customers/${customerId}/archive-preview`, {
      method: 'GET',
      $autoCancel: false,
    })) as CustomerArchivePreview
  }

  async function archiveCustomer(customerId: string, input: ArchiveCustomerInput) {
    await pb.send(`/api/customers/${customerId}/archive`, {
      method: 'POST',
      body: input,
      $autoCancel: false,
    })
    await Promise.all([
      refreshCustomer(customerId),
      input.targetId ? refreshCustomer(input.targetId) : Promise.resolve(),
    ])
    await refetchAfterRelink()
  }

  async function unarchiveCustomer(customerId: string) {
    await pb.send(`/api/customers/${customerId}/unarchive`, {
      method: 'POST',
      body: {},
      $autoCancel: false,
    })
    await refreshCustomer(customerId)
  }

  async function relinkServiceCustomer(
    collection: ServiceCollectionName,
    serviceId: string,
    customerId: string,
    reason: string,
  ) {
    await pb.send(`/api/services/${collection}/${serviceId}/relink-customer`, {
      method: 'POST',
      body: { customerId, reason },
      $autoCancel: false,
    })
    await refreshCustomer(customerId)
    await refetchAfterRelink()
  }

  /** Resources changed owner server-side; refetch whatever is cached. */
  async function refetchAfterRelink() {
    await opts.queryClient.invalidateQueries({ queryKey: ['pocketbase'] })
  }

  const followUpSortFields: Record<FollowUpSearchSortKey, string> = {
    due: 'due_at',
    title: 'title',
    created: 'created',
  }

  async function searchFollowUps(
    input: FollowUpsSearchInput,
  ): Promise<FollowUpsSearchResult> {
    const filters = input.filters ?? {}
    const parts: string[] = []
    // Date params go through pb.filter as Date objects: the SDK serialises
    // them in PocketBase's space-separated spelling, so same-day comparisons
    // against stored values stay correct (see the timestamp gotcha).
    const params: Record<string, unknown> = {}

    if (input.scope === 'archive') {
      parts.push("status = 'closed'")
    } else {
      // "Upcoming" is day-granular ZA time, same as isDue: due from midnight
      // tonight onward. The due slice below that boundary is synced locally.
      parts.push("status = 'open' && due_at >= {:upcoming}")
      params.upcoming = new Date(upcomingBoundaryUtcIso())
    }
    if (filters.customer?.trim()) {
      parts.push('customer_id.full_name ~ {:customer}')
      params.customer = filters.customer.trim()
    }
    if (filters.assigneeId) {
      parts.push('assignee_id = {:assigneeId}')
      params.assigneeId = filters.assigneeId
    }
    if (filters.categoryId) {
      parts.push('category_id = {:categoryId}')
      params.categoryId = filters.categoryId
    }
    if (filters.title?.trim()) {
      parts.push('title ~ {:title}')
      params.title = filters.title.trim()
    }

    const sort = input.sort ?? defaultFollowUpSort(input.scope)
    const result = await pb
      .collection('follow_ups')
      .getList<PbFollowUpRecord>(input.page, input.pageSize, {
        filter: pb.filter(parts.join(' && '), params),
        sort: `${sort.dir === 'desc' ? '-' : ''}${followUpSortFields[sort.key]}`,
        // The tab-count search and a page search run concurrently against
        // this same endpoint; the SDK's auto-cancellation would kill one.
        requestKey: null,
      })

    const followUps = result.items.map(pbFollowUpToFollowUp)
    await upsertWhenReady(followUpsCollection, followUps)

    return {
      keys: followUps.map((followUp) => followUp.id),
      page: result.page,
      pageSize: result.perPage,
      totalItems: result.totalItems,
      totalPages: result.totalPages,
    }
  }

  /**
   * A short-lived token for reading protected files (customer documents).
   * Minted per open — the token outlives a click, not a session.
   */
  function getFileToken(): Promise<string> {
    return pb.files.getToken()
  }

  /**
   * Loads one customer's follow-ups — all of them, regardless of due date or
   * status — into the local collection. The customer page needs the upcoming
   * and closed ones the due-only sync no longer carries, and a single
   * customer's list is bounded where the global one is not.
   */
  async function loadCustomerFollowUps(customerId: string): Promise<void> {
    const records = await pb
      .collection('follow_ups')
      .getFullList<PbFollowUpRecord>({
        sort: 'due_at',
        filter: pb.filter('customer_id = {:customerId}', { customerId }),
        // Runs alongside the collection's own sync fetch on first mount —
        // don't let the SDK auto-cancel either of them.
        requestKey: null,
      })
    await upsertWhenReady(followUpsCollection, records.map(pbFollowUpToFollowUp))
  }

  /**
   * Loads one service (typically archived, since those are excluded from the
   * live sync) plus its notes, status changes and — for jobs — assignments
   * into the local collections, so detail pages and their mutations work.
   * Accepts either a record id or a public id. Returns whether it was found.
   */
  /**
   * Customer-scoped load of the given services' work notes into the per-type
   * note collections — the History tab reads them back through live queries.
   *
   * The collections' own sync can miss these notes: its active-parent filter
   * only widens to hydrated services at queryFn time, so a parent hydrated
   * after the sync started (or hydrated without children via hydrateRecord)
   * has no notes locally until this backfill lands them.
   */
  async function loadServiceNotes(
    services: Array<{ type: 'callout' | 'session' | 'job'; id: string }>,
  ): Promise<void> {
    const idsByType = new Map<'callout' | 'session' | 'job', string[]>()
    for (const service of services) {
      const ids = idsByType.get(service.type) ?? []
      ids.push(service.id)
      idsByType.set(service.type, ids)
    }

    await Promise.all(
      [...idsByType.entries()].map(async ([type, ids]) => {
        const relation =
          type === 'job'
            ? ('job_id' as const)
            : type === 'callout'
              ? ('callout_id' as const)
              : ('session_id' as const)
        const records = await pb
          .collection(`${type}_notes`)
          .getFullList<RecordModel>({
            sort: 'created',
            filter: ids.map((id) => `${relation}="${id}"`).join(' || '),
            // Runs alongside the note collection's own sync fetch, which the
            // history live queries kick off at the same moment — don't let
            // the SDK auto-cancel either of them.
            requestKey: null,
          })
        const target =
          type === 'job'
            ? jobNotesCollection
            : type === 'callout'
              ? notesCollection
              : sessionNotesCollection
        await upsertWhenReady(
          target,
          records.map((record) => pbNoteToNote(record as never, pb, relation)),
        )
      }),
    )
  }

  async function hydrateServiceDetails(
    type: 'callout' | 'session' | 'job',
    identity: string,
  ): Promise<boolean> {
    const token = pb.authStore.token
    if (!token || token.startsWith('demo_') || !identity.trim()) return false

    return singleFlight(`details:${type}:${identity}`, () =>
      doHydrateServiceDetails(type, identity),
    )
  }

  async function doHydrateServiceDetails(
    type: 'callout' | 'session' | 'job',
    identity: string,
  ): Promise<boolean> {
    const collectionName =
      type === 'job' ? 'jobs' : type === 'callout' ? 'callouts' : 'sessions'

    let record: RecordModel
    try {
      record = await pb
        .collection(collectionName)
        .getFirstListItem<RecordModel>(
          pb.filter('id = {:identity} || public_id = {:identity}', { identity }),
        )
    } catch {
      return false
    }

    await upsertServiceRecords(type, [record], { keepAcrossRefetch: true })

    const relation =
      type === 'job' ? 'job_id' : type === 'callout' ? 'callout_id' : 'session_id'
    const childFilter = pb.filter(`${relation} = {:id}`, { id: record.id })

    const loadChildren: Promise<void>[] = [
      (async () => {
        const notes = await pb
          .collection(`${type}_notes`)
          .getFullList<RecordModel>({ sort: '-updated', filter: childFilter })
        const target =
          type === 'job'
            ? jobNotesCollection
            : type === 'callout'
              ? notesCollection
              : sessionNotesCollection
        await upsertWhenReady(
          target,
          notes.map((note) => pbNoteToNote(note as never, pb, relation)),
        )
      })(),
      (async () => {
        const changes = await pb
          .collection(`${type}_status_changes`)
          .getFullList<RecordModel>({ sort: '-created', filter: childFilter })
        const target =
          type === 'job'
            ? jobStatusChangesCollection
            : type === 'callout'
              ? statusChangesCollection
              : sessionStatusChangesCollection
        await upsertWhenReady(
          target,
          changes.map((change) => pbStatusChangeToStatusChangeLog(change as never, relation)),
        )
      })(),
      (async () => {
        const addendums = await pb
          .collection(`${type}_addendums`)
          .getFullList<RecordModel>({ sort: '-created', filter: childFilter })
        const target =
          type === 'job'
            ? jobAddendumsCollection
            : type === 'callout'
              ? addendumsCollection
              : sessionAddendumsCollection
        await upsertWhenReady(
          target,
          addendums.map((addendum) => pbAddendumToAddendum(addendum as never, relation)),
        )
      })(),
      (async () => {
        const revisions = await pb
          .collection(`${type}_revisions`)
          .getFullList<RecordModel>({ sort: '-created', filter: childFilter })
        const target =
          type === 'job'
            ? jobRevisionsCollection
            : type === 'callout'
              ? calloutRevisionsCollection
              : sessionRevisionsCollection
        await upsertWhenReady(
          target,
          revisions.map((revision) =>
            pbServiceRevisionToServiceRevision(revision as never, relation),
          ),
        )
      })(),
    ]

    loadChildren.push(
      (async () => {
        if (type === 'job') {
          const assignments = await pb
            .collection('job_assignments')
            .getFullList<PbJobAssignmentRecord>({ sort: '-created', filter: childFilter })
          await upsertWhenReady(
            jobAssignmentsCollection,
            assignments.map(pbJobAssignmentToJobAssignment),
          )
          return
        }
        const assignments = await pb
          .collection(`${type}_assignments`)
          .getFullList<PbServiceAssignmentRecord>({ sort: '-created', filter: childFilter })
        const target =
          type === 'callout' ? calloutAssignmentsCollection : sessionAssignmentsCollection
        await upsertWhenReady(
          target,
          assignments.map((assignment) =>
            pbServiceAssignmentToServiceAssignment(
              assignment,
              relation as 'callout_id' | 'session_id',
            ),
          ),
        )
      })(),
    )

    await Promise.all(loadChildren)
    return true
  }

  /**
   * Loads one record by id into its collection (getOne + writeUpsert), so a
   * command can mutate a record this client hasn't downloaded — without
   * refetching the whole collection. Returns whether the record was found.
   */
  async function hydrateRecord(
    kind: 'customer' | 'job' | 'callout' | 'session',
    id: string,
  ): Promise<boolean> {
    const token = pb.authStore.token
    if (!token || token.startsWith('demo_') || !id.trim()) return false

    if (kind === 'customer') {
      // Customers sit outside the service funnel: no active-only filter to
      // widen, and their collection syncs the full list anyway.
      return singleFlight(`record:customer:${id}`, async () => {
        try {
          const record = await pb.collection('customers').getOne<RecordModel>(id)
          await upsertWhenReady(
            customersCollection,
            pbCustomerToCustomer(record as unknown as PbCustomerRecord),
          )
          return true
        } catch {
          return false
        }
      })
    }

    const collectionName =
      kind === 'job' ? 'jobs' : kind === 'callout' ? 'callouts' : 'sessions'

    return singleFlight(`record:${kind}:${id}`, async () => {
      try {
        const record = await pb.collection(collectionName).getOne<RecordModel>(id)
        // keepAcrossRefetch: a hydrated archived service must survive later
        // refetches of the active-only queries.
        await upsertServiceRecords(kind, [record], { keepAcrossRefetch: true })
        return true
      } catch {
        return false
      }
    })
  }

  async function updateCustomerDirect(
    id: string,
    patch: Partial<Customer>,
  ): Promise<Customer> {
    let current: Customer
    try {
      current = pbCustomerToCustomer(
        await pb.collection('customers').getOne<PbCustomerRecord>(id),
      )
    } catch (err) {
      throw new Error(`Customer ${id} not found`, { cause: err })
    }

    const result = await pb
      .collection('customers')
      .update<PbCustomerRecord>(id, customerToPbInput({ ...current, ...patch }))
    const record = requirePbRecord(
      'customers',
      'update',
      id,
      result as PbMutationResult,
    ) as unknown as PbCustomerRecord
    const updated = pbCustomerToCustomer(record)

    // No revision handling here: the customer-revisions server hook appends
    // the trail inside the update request, for this path like every other.

    // Writing into an uninitialized collection throws (see
    // attachKioskSessionsRealtime); only mirror when sync is running.
    if (customersCollection.isReady()) {
      customersCollection.utils.writeUpsert(updated)
    }
    return updated
  }

  // ---------------------------------------------------------------------------
  // Realtime: one SSE connection, every collection subscribed, and events used
  // only as dirty signals — "collection X changed" — that refetch whatever is
  // currently in view. Nothing is merged from event payloads, so the slice
  // filters, echo handling and ordering all stay the queryFn's problem. See
  // docs/adr/0002-realtime-as-refetch-signal.md and ./realtimeRefetch.ts.
  //
  // The one exception is kiosk_sessions, which mirrors payloads into its
  // collection: the tablet and the receptionist panel edit the same session
  // and need field-level latency, and that UI already carries an echo
  // protocol (last_update_origin) for it.
  // ---------------------------------------------------------------------------

  /** PocketBase collection → the in-memory collections it feeds. */
  const realtimeSignals: ReadonlyArray<readonly [string, RefetchTarget[]]> = [
    [authCollection, [usersCollection]],
    ['follow_up_categories', [followUpCategoriesCollection]],
    ['customers', [customersCollection]],
    ['customer_notes', [customerNotesCollection]],
    ['customer_documents', [customerDocumentsCollection]],
    ['customer_revisions', [customerRevisionsCollection]],
    ['jobs', [jobsCollection]],
    ['callouts', [calloutsCollection]],
    ['sessions', [sessionsCollection]],
    ['job_notes', [jobNotesCollection]],
    ['callout_notes', [notesCollection]],
    ['session_notes', [sessionNotesCollection]],
    ['job_status_changes', [jobStatusChangesCollection]],
    ['callout_status_changes', [statusChangesCollection]],
    ['session_status_changes', [sessionStatusChangesCollection]],
    ['job_addendums', [jobAddendumsCollection]],
    ['callout_addendums', [addendumsCollection]],
    ['session_addendums', [sessionAddendumsCollection]],
    ['job_revisions', [jobRevisionsCollection]],
    ['callout_revisions', [calloutRevisionsCollection]],
    ['session_revisions', [sessionRevisionsCollection]],
    ['job_assignments', [jobAssignmentsCollection]],
    ['callout_assignments', [calloutAssignmentsCollection]],
    ['session_assignments', [sessionAssignmentsCollection]],
    ['follow_ups', [followUpsCollection]],
    ['documents', [documentsCollection]],
    ['whatsapp_messages', [whatsappMessagesCollection]],
    ['customer_reminders', [customerRemindersCollection]],
    ['app_settings', [appSettingsCollection]],
    ['branding', [brandingCollection]],
    ['kiosks', [kiosksCollection]],
    ['kiosk_tablet_users', [kioskPairedDevicesCollection]],
  ]
  const allRealtimeTargets: RefetchTarget[] = realtimeSignals.flatMap(
    ([, targets]) => targets,
  )

  function mirrorKioskSessionEvent(event: {
    action: string
    record?: PbKioskSessionRecord
  }) {
    const record = event.record
    if (!record?.id) return

    // This subscription is attached app-wide, but the collection only
    // starts syncing once a kiosk view subscribes to it. Manual writes on
    // an uninitialized collection throw SyncNotInitializedError; dropping
    // the event is safe — sync init refetches the full list via queryFn.
    if (!kioskSessionsCollection.isReady()) return

    if (event.action === 'delete') {
      kioskSessionsCollection.utils.writeDelete(record.id)
      return
    }

    // Always upsert so the synced baseline advances (including for our own
    // writes); otherwise TanStack would revert to a stale baseline when the
    // optimistic mutation settles. The kiosk UI uses last_update_origin to
    // ignore echoes of its own writes and protect locally typed fields.
    kioskSessionsCollection.utils.writeUpsert(pbKioskSessionToKioskSession(record))
  }

  interface RealtimeSession {
    scheduler: RefetchScheduler
    cleanups: Array<() => void>
  }
  let realtimeSession: RealtimeSession | null = null

  /**
   * Opens the realtime connection and subscribes every collection. Idempotent
   * while attached; call detachRealtime() on logout or token change. All
   * subscribe calls are issued before awaiting so the SDK submits them in the
   * single request that follows PB_CONNECT.
   */
  async function attachRealtime(): Promise<void> {
    const token = pb.authStore.token
    if (!token || token.startsWith('demo_') || realtimeSession) return

    const scheduler = createRefetchScheduler({
      onError: (id, error) => {
        console.warn('Realtime refetch failed', { collection: id, error })
      },
    })
    const session: RealtimeSession = { scheduler, cleanups: [] }
    realtimeSession = session

    const flushAll = () => scheduler.flush(allRealtimeTargets)

    // Catch-up after a gap. PB_CONNECT fires on every (re)connect; the first
    // one lands while collections are freshly fetched, so only later ones
    // mean events may have been missed.
    let connectedOnce = false
    const subscriptions: Array<Promise<unknown>> = [
      pb.realtime.subscribe('PB_CONNECT', () => {
        if (connectedOnce) flushAll()
        connectedOnce = true
      }),
      pb
        .collection('kiosk_sessions')
        .subscribe<PbKioskSessionRecord>('*', mirrorKioskSessionEvent),
      ...realtimeSignals.map(([collection, targets]) =>
        pb.collection(collection).subscribe('*', () => {
          for (const target of targets) scheduler.mark(target)
        }),
      ),
    ]

    try {
      await Promise.all(subscriptions)
    } catch (error) {
      // Detached while connecting (StrictMode remounts, quick logout): the
      // teardown already dropped these subscriptions; nothing to report.
      if (realtimeSession !== session) return
      throw error
    }
    if (realtimeSession !== session) return

    // A backgrounded tab or a laptop lid can lose events without the SSE
    // stream noticing for a while; treat regaining focus/network as a gap.
    if (typeof window !== 'undefined') {
      const onVisible = () => {
        if (document.visibilityState === 'visible') flushAll()
      }
      document.addEventListener('visibilitychange', onVisible)
      window.addEventListener('online', flushAll)
      session.cleanups.push(() => {
        document.removeEventListener('visibilitychange', onVisible)
        window.removeEventListener('online', flushAll)
      })
    }
  }

  function detachRealtime(): void {
    const session = realtimeSession
    if (!session) return
    realtimeSession = null
    session.scheduler.dispose()
    for (const cleanup of session.cleanups) cleanup()
    // No topic = every subscription; the SDK closes the stream with the last.
    void pb.realtime.unsubscribe().catch((error) => {
      console.warn('Failed to close realtime connection', error)
    })
  }

  async function hydrateUsers() {
    const token = pb.authStore.token
    if (!token || token.startsWith('demo_')) return
    await usersCollection.preload()
    await followUpCategoriesCollection.preload()
  }

  // Fetch a single job straight from the server (getOne), returning the mapped
  // record — used when composing depends on server-assigned fields (publicId)
  // that the local collection may not yet hold.
  async function getFreshJob(jobId: string): Promise<Job | undefined> {
    const token = pb.authStore.token
    if (!token || token.startsWith('demo_')) return jobsCollection.get?.(jobId)
    let record: RecordModel
    try {
      record = await pb.collection('jobs').getOne<RecordModel>(jobId)
    } catch {
      return jobsCollection.get?.(jobId)
    }
    // Best-effort cache refresh; never let it mask the fetched record.
    try {
      await upsertServiceRecords('job', [record])
    } catch {
      // ignore — the returned job is what matters
    }
    return pbJobToJob(record as unknown as PbJobRecord)
  }

  // Ask the server to pull the latest Content templates from Twilio by bumping
  // the sync-trigger row (adapter gateway). The hook syncs synchronously, so by
  // the time this resolves the cache is fresh — reload it into the collection.
  async function requestTwilioTemplateSync(): Promise<{
    ok: boolean
    result?: string
  }> {
    const res = (await pb
      .collection('whatsapp_twilio_sync')
      .update('watwiliosync001', {
        requested_at: new Date().toISOString(),
      })) as { last_result?: string }

    const records = await pb
      .collection('whatsapp_twilio_templates')
      .getFullList<PbWhatsAppTwilioTemplateRecord>({ sort: 'friendly_name' })
    for (const record of records) {
      whatsappTwilioTemplatesCollection.utils.writeUpsert(
        pbWhatsAppTwilioTemplateToTemplate(record),
      )
    }

    const result = res?.last_result ?? ''
    return { ok: result.startsWith('ok'), result }
  }

  // Twilio connection settings go through the admin-gated server routes
  // (pb_hooks/whatsapp-twilio-settings.pb.js): the whatsapp_twilio collection
  // itself is superuser-only so the auth token never reaches the browser.
  async function loadWhatsAppTwilioSettings(): Promise<WhatsAppTwilioSettings> {
    return (await pb.send('/api/whatsapp/twilio-settings', {
      method: 'GET',
      $autoCancel: false,
    })) as WhatsAppTwilioSettings
  }

  async function saveWhatsAppTwilioSettings(
    input: SaveWhatsAppTwilioSettingsInput,
  ): Promise<WhatsAppTwilioSettings> {
    return (await pb.send('/api/whatsapp/twilio-settings', {
      method: 'POST',
      body: {
        accountSid: input.accountSid,
        fromNumber: input.fromNumber,
        messagingServiceSid: input.messagingServiceSid,
        authToken: input.authToken ?? '',
        clearAuthToken: input.clearAuthToken === true,
      },
      $autoCancel: false,
    })) as WhatsAppTwilioSettings
  }

  return {
    requestTwilioTemplateSync,
    loadWhatsAppTwilioSettings,
    saveWhatsAppTwilioSettings,
    getFreshJob,
    whatsappTemplatesCollection,
    whatsappTwilioTemplatesCollection,
    usersCollection,
    followUpCategoriesCollection,
    customersCollection,
    jobsCollection,
    kiosksCollection,
    kioskSessionsCollection,
    documentsCollection,
    kioskPairedDevicesCollection,
    calloutsCollection,
    sessionsCollection,
    notesCollection,
    sessionNotesCollection,
    jobNotesCollection,
    customerNotesCollection,
    customerDocumentsCollection,
    followUpsCollection,
    statusChangesCollection,
    sessionStatusChangesCollection,
    jobStatusChangesCollection,
    addendumsCollection,
    sessionAddendumsCollection,
    jobAddendumsCollection,
    customerRevisionsCollection,
    calloutRevisionsCollection,
    sessionRevisionsCollection,
    jobRevisionsCollection,
    jobAssignmentsCollection,
    calloutAssignmentsCollection,
    sessionAssignmentsCollection,
    whatsappMessagesCollection,
    appSettingsCollection,
    customerRemindersCollection,
    stopCustomerReminder,
    resumeCustomerReminder,
    brandingCollection,
    saveBranding,
    usersLookup,
    archivedServicesCollection,
    searchArchivedServices,
    searchCustomers,
    searchFollowUps,
    loadCustomerFollowUps,
    getFileToken,
    loadServiceNotes,
    hydrateServiceDetails,
    hydrateUsers,
    hydrateRecord,
    updateCustomerDirect,
    previewCustomerArchive,
    archiveCustomer,
    unarchiveCustomer,
    relinkServiceCustomer,
    attachRealtime,
    detachRealtime,
  }
}
